<!-- Part of the SPARKL educational activity system, Copyright 2019 by Pepper Williams -->
<template>
<div 
	v-show="!quick_look_only"
	class="k-resource-collection-item elevation-3"
	:class="card_class" 
	:style="{'background-color': color_for_asset_type}" 
	role="button"
	tabindex="0"
	:aria-label="card_accessible_label"
	:aria-haspopup="card_has_popup ? 'dialog' : undefined"
	:aria-expanded="card_expanded ? 'true' : 'false'"
	@click="card_clicked"
	@keydown="card_keydown"
>
	<k-hover v-slot:default="{hover}">
		<div class="k-resource-collection-item--title">
			<div class="k-resource-collection-item-type-icon">
				<k-icon :color="icon_color(hover)" small>fas {{icon}}</k-icon>
			</div>

			<k-tooltip top>
				<template v-slot:activator="{on}">
					<k-icon v-on="on" v-show="item_created_by_this_user && !item_to_show.agency_sanctioned" style="font-size:12px; margin-right:4px;">fas fa-circle-user</k-icon>
				</template>
				{{user_created_item_tooltip}}
			</k-tooltip>

			<span v-html="item_title" class="k-resource-collection-item-title-link"></span>
			
			<div v-if="show_new_indicator" class="k-new-indicator mr-2 d-flex" :class="{'k-tile': !full_width_resource}">NEW <k-icon color="white" class="ml-1" x-small>fas fa-sparkles</k-icon></div>

			<k-tooltip v-if="unpublished_display" top>
				<template v-slot:activator="{on}">
					<k-btn v-on="on" v-show="full_width_resource" color="tertiary" x-small depressed @click.prevent.stop="show_hqim_status">Unpublished</k-btn>
				</template>
				This {{ item_type }} is <b>unpublished</b>.<br/>It will not show in searches and only appears in collections to editors of the collection.
			</k-tooltip>

			<div v-if="item_to_show.standards && item_to_show.standards.length>0" v-show="full_width_resource" class="k-resource-collection-item-standards-wrapper" @click.stop="">
				<div v-if="item_to_show.standards.length>default_max_standards_showing" style="margin-right:2px;">
					<k-btn v-if="show_all_standards" style="font-size:12px; min-width:20px;" class="px-0" x-small text color="#333" @click="show_all_standards=false">
						<k-icon style="font-size:10px" x-small>fas fa-chevrons-right</k-icon>
					</k-btn>

					<k-btn v-else color="#333" class="px-0" style="font-size:12px" x-small text @click="show_all_standards=true">
						+{{item_to_show.standards.length-default_max_standards_showing}}<k-icon style="margin-left:3px; font-size:10px;">fas fa-chevrons-left</k-icon>
					</k-btn>
				</div>

				<CASEItemBtn
					v-show="i < default_max_standards_showing || show_all_standards"
					v-for="(s, i) in item_to_show.standards"
					satchel_determines_framework_id="yes"
					star_primary_alignments="false"
					:key="s.identifier"
					:btn_color="s.btn_color(item_to_show.standards)"
					:outer_class="'ml-1'"
					:item="s"
					:selected_items="item_to_show.standards"
					:framework_identifier="s.framework_identifier"
					x-small
				/>
			</div>

			<!-- In situations where you can remove the item, we want to show the item's date. this includes the default collection, a shadow unit item, and when you're editing a unit -->
			<div
				v-if="(viewing_my_default_collection || viewing_my_shadow_unit || option_availability.remove || from_archived) && full_width_resource && resource_date_display"
				class="k-resource-collection-item-created-date"
				:class="resource_date_display_class"
				v-html="resource_date_display"
			></div>

			<div
				v-if="!from_pd_resource_popup_collection && !lti_chooser"
				class="k-resource-collection-item-menu-btn"
			>
				<k-menu offset-y bottom right accessible-popover accessible-label="Resource options" :content-id="`resource-collection-item-options-panel-${item_id}`">
					<template v-slot:activator="{on, attrs}">
						<k-btn v-visible="hover || full_width_resource" v-on="on" v-bind="attrs" icon small color="#000" accessible-label="Open resource options" @click="mark_as_last_viewed_resource">
							<k-icon small>fas fa-ellipsis-v</k-icon>
						</k-btn>
					</template>

					<k-list dense>
						<k-list-item @click="card_clicked" style="border-bottom:1px solid #ccc">
							<k-list-item-icon>
								<k-icon small color="#000">fas fa-fw {{icon}} fa-beat</k-icon>
							</k-list-item-icon>
							<k-list-item-title>
								<b>Open {{item_noun(true)}}</b>
							</k-list-item-title>
						</k-list-item>

						<k-list-item v-if="option_availability.open_in_new_window" @click="open_resource_in_new_window">
							<k-list-item-icon>
								<k-icon small>fas fa-fw fa-arrow-up-right-from-square</k-icon>
							</k-list-item-icon>
							<k-list-item-title>Open {{item_noun(true)}} in a new window</k-list-item-title>
						</k-list-item>

						<k-list-item @click="copy_link_to_clipboard">
							<k-list-item-icon>
								<k-icon small>fas fa-fw fa-link</k-icon>
							</k-list-item-icon>
							<k-list-item-title>Copy {{item_noun(true)}} link to clipboard</k-list-item-title>
						</k-list-item>

						<k-list-item v-if="option_availability.report_issue" @click="report_issue">
							<k-list-item-icon>
								<k-icon small>fas fa-fw fa-triangle-exclamation</k-icon>
							</k-list-item-icon>
							<k-list-item-title>Provide feedback about this {{item_noun(true)}}</k-list-item-title>
						</k-list-item>

						<k-list-item v-if="option_availability.create_sparkl_from_resource" @click="create_activity_from_resource">
							<k-list-item-icon>
								<k-icon small color="green">fas fa-fw fa-star</k-icon>
							</k-list-item-icon>
							<k-list-item-title>Create a new Student Activity with this resource</k-list-item-title>
						</k-list-item>

						<k-list-item v-if="option_availability.add_to_gc" @click="add_to_gc">
							<k-list-item-icon>
								<img src="./../../images/google_classroom_logo.png" alt="" aria-hidden="true" style="height: 18px; margin-top: 3px; margin-left: -4px; border-radius: 3px;">
							</k-list-item-icon>
							<k-list-item-title>Add to Google Classroom</k-list-item-title>
						</k-list-item>

						<k-list-item v-if="option_availability.open_in_heyc" @click="open_in_heyc">
							<k-list-item-icon>
								<img src="./../../images/heyc.svg" alt="" aria-hidden="true" style="height: 16px; margin-left: -8px; margin-top:3px;">
							</k-list-item-icon>
							<k-list-item-title>{{(site_config.heyc_resource_menu_text || 'Open in HeyC')}}</k-list-item-title>
						</k-list-item>

						<k-list-item v-if="option_availability.import_to_district" @click="share_item(option_availability.import_to_district)">
							<k-list-item-icon>
								<k-icon small color="indigo">fas fa-fw fa-share-nodes</k-icon>
							</k-list-item-icon>
							<k-list-item-title>Import {{item_noun(true)}} to {{ option_availability.import_to_district }}</k-list-item-title>
						</k-list-item>

						<k-list-item v-if="option_availability.share" @click="share_item('here')">
							<k-list-item-icon>
								<k-icon small color="purple">fas fa-fw fa-share-nodes</k-icon>
							</k-list-item-icon>
							<k-list-item-title>Share {{is_lesson?'Lesson':(is_sparkl?'Activity':'Resource')}} with other educators</k-list-item-title>
						</k-list-item>

						<k-list-item v-if="item_copy_for_user" class="align-start">
							<k-list-item-icon style="margin-top:-4px">
								<k-icon color="green darken-2" small>fas fa-fw fa-cubes-stacked</k-icon>
							</k-list-item-icon>
							<k-list-item-title>
								<i>You have copied this {{item_noun(true)}} for your use.</i>
								<div v-if="option_availability.show_original" class="mt-2 mb-1" style="cursor:pointer" @click="show_original">
									<k-icon color="green darken-2" class="mr-2" small>fas fa-fw fa-clock-rotate-left</k-icon>
									Show original item
									<span v-if="is_sparkl">(A{{item.url}})</span>
									<span v-if="is_lesson">(L{{item.lesson_id}})</span>
								</div>
							</k-list-item-title>
						</k-list-item>
						
						<k-list-item v-if="option_availability.add_to_lesson" @click="add_to_open_lesson">
							<k-list-item-icon>
								<k-icon small color="blue darken-2">fas fa-fw fa-circle-plus</k-icon>
							</k-list-item-icon>
							<k-list-item-title>Add {{item_noun(true)}} to open Lesson</k-list-item-title>
						</k-list-item>

						<k-list-item v-if="option_availability.edit" @click="edit_item_start">
							<k-list-item-icon>
								<k-icon small color="blue darken-2">fas fa-fw fa-edit</k-icon>
							</k-list-item-icon>
							<k-list-item-title>Edit {{is_lesson?'Lesson':(is_sparkl?'Activity':'Resource')}}</k-list-item-title>
						</k-list-item>

						<k-list-item v-if="option_availability.duplicate" @click="duplicate_item">
							<k-list-item-icon>
								<k-icon small color="teal">fas fa-fw fa-copy</k-icon>
							</k-list-item-icon>
							<k-list-item-title>Duplicate {{item_noun(true)}}</k-list-item-title>
						</k-list-item>

						<k-list-item v-if="item_to_show.restricted || option_availability.show_restricted_resources_control" @click="edit_item_restriction">
							<k-list-item-icon>
								<k-icon small :color="item_to_show.restricted ? 'red darken-3' : 'green darken-3'">fas fa-fw {{item_to_show.restricted?'fa-lock':'fa-lock-open'}}</k-icon>
							</k-list-item-icon>
							<k-list-item-title>
								<span v-if="item_to_show.restricted"><b>Restricted</b> to authorized teachers and staff <k-icon v-if="option_availability.show_restricted_resources_control" color="primary" small class="ml-2" style="margin-top:-3px">fas fa-fw fa-edit</k-icon></span>
								<span v-if="!item_to_show.restricted">Not restricted to authorized users <k-icon color="primary" small class="ml-2" style="margin-top:-3px">fas fa-fw fa-edit</k-icon></span>
							</k-list-item-title>
						</k-list-item>

						<k-list-item v-if="option_availability.remove" @click="$emit('remove_item', item)">
							<k-list-item-icon>
								<k-icon small color="red darken-2">fas fa-fw fa-trash-alt</k-icon>
							</k-list-item-icon>
							<k-list-item-title>Remove {{is_lesson?'Lesson':(is_sparkl?'Activity':'Resource')}} {{ in_lesson_editor ? ' From Lesson' : '' }}</k-list-item-title>
						</k-list-item>

						<k-list-item v-if="option_availability.unpublish" @click="unpublish_item">
							<k-list-item-icon>
								<k-icon small color="red darken-2">fas fa-fw fa-ban</k-icon>
							</k-list-item-icon>
							<k-list-item-title>“Unpublish” {{is_lesson?'Lesson':(is_sparkl?'Activity':'Resource')}}</k-list-item-title>
						</k-list-item>

						<k-list-item v-if="option_availability.publish_status" @click="show_hqim_status">
							<k-list-item-icon>
								<k-icon color="#4d226c" small>fas fa-fw fa-file-certificate</k-icon>
							</k-list-item-icon>
							<k-list-item-title>{{hqim_menu_text}}</k-list-item-title>
						</k-list-item>

						<k-list-item v-if="is_sparkl" style="border-top:1px solid #ccc"><k-list-item-title>{{site_config.sparkl_app_name}} Activity ID: A{{item_to_show.url}}</k-list-item-title></k-list-item>
						<k-list-item v-if="is_sparkl_bank" style="border-top:1px solid #ccc"><k-list-item-title>{{item_noun(true)}} ID: A{{item.url}}</k-list-item-title></k-list-item>
						<k-list-item v-if="is_lesson" style="border-top:1px solid #ccc"><k-list-item-title>Lesson ID: L{{item_to_show.lesson_id}}</k-list-item-title></k-list-item>
						<k-list-item v-if="is_non_sparkl_resource && !is_sparkl_bank && !is_temp_resource" style="border-top:1px solid #ccc" @click="copy_reslink_to_clipboard"><k-list-item-title>Resource ID: R{{item_to_show.resource_id}}<k-icon x-small class="ml-1" style="margin-top:-3px">fas fa-link</k-icon></k-list-item-title></k-list-item>
						<k-list-item v-if="from_collection_string" style="border-top:1px solid #ccc"><k-list-item-title>From: {{from_collection_string}}</k-list-item-title></k-list-item>
						<k-list-item v-if="from_other_unit_string" style="border-top:1px solid #ccc"><k-list-item-title>{{from_other_unit_string}}</k-list-item-title></k-list-item>
						<!-- show "warning" text for temp resources? not for now -->
						<!-- <k-list-item v-if="is_temp_resource" style="border-top:1px solid #ccc"><k-list-item-title><i>This resource is linked directly in the Lesson Plan text</i></k-list-item-title></k-list-item> -->

						<k-list-item v-if="from_archived" @click="$emit('unarchive_item', item)">
							<k-list-item-icon>
								<k-icon small color="green">fas fa-fw fa-heart-pulse</k-icon>
							</k-list-item-icon>
							<k-list-item-title>Unarchive this item</k-list-item-title>
						</k-list-item>
					</k-list>
				</k-menu>
			</div>

			<!-- btns for lti_chooser -->
			<!-- NOTE: if we show the preview btn here, we need to uncomment the css where it says “make room for the preview button in the lti chooser” below -->
			<!-- NOTE: the preview btn will be hidden in tile mode by the k-resource-folder-resource-item-tile-wrapper class -->
			<div v-if="lti_chooser" class="k-resource-collection-item-menu-btn-preview"><k-tooltip bottom><template v-slot:activator="{on}">
				<k-btn v-on="on" icon small color="#000" :accessible-label="`Preview ${item_noun(true)}`" @click.stop="card_clicked"><k-icon small>fas fa-glasses</k-icon></k-btn>
			</template>Preview {{item_noun(true)}}</k-tooltip></div>
			<div v-if="lti_chooser&&!is_lesson&&!is_sparkl_bank" class="k-resource-collection-item-menu-btn"><k-tooltip bottom><template v-slot:activator="{on}">
				<k-btn v-on="on" icon small color="#000" accessible-label="Choose activity" @click.stop="lti_chooser_choose"><k-icon>fas fa-circle-check</k-icon></k-btn>
			</template>Choose {{item_noun(true)}}</k-tooltip></div>

			<div v-if="item.todo && !in_unit_editor" class="k-resource-collection-item-todo-checkbox">
				<k-btn v-if="!todo_complete" icon x-small color="#333" accessible-label="Mark assignment complete" @click.stop="mark_todo_assignment_complete"><k-icon class="k-resource-link-check-box">far fa-square</k-icon></k-btn>
				<k-btn v-else icon x-small color="#333" accessible-label="Mark assignment incomplete" @click.stop="complete_todo_assignment_clicked"><k-icon class="k-resource-link-check-box">fas fa-check-square</k-icon></k-btn>
			</div>
		</div>
	</k-hover>

	<k-dialog
		v-if="lesson_showing"
		v-model="lesson_showing" 
		:max-width="item_to_show.lp_variant == 'B' ? 1200 : 900"
		:hide-overlay="item_shifted"
		:no-click-animation="item_shifted"
		:retain-focus="!item_shifted"
		:content-class="`k-lpe-editor-dialog k-resource-collection-item-lesson-card-dialog ${item_shifted && !quick_look_shifted ? 'k-resource-collection-lesson-item-card-dialog-shifted' : ''}`"
		:persistent="item_shifted"
		scrollable
	>
		<k-card class="k-resource-collection-item-lesson-card">
			<div class="d-flex align-center pb-2" :style="item_to_show.lp_variant=='A'?'border-bottom:1px solid #999;':''">
				<k-icon class="mr-2">fas fa-rectangle-list</k-icon>
				<div class="k-lesson-title" style="font-weight:bold; font-weight:900" v-html="item_title"></div>
				<k-spacer/>
				<div v-if="show_new_indicator" class="k-new-indicator-quick-look d-flex mr-2">NEW <k-icon color="white" class="ml-1" x-small>fas fa-sparkles</k-icon></div>
				<div v-if="!($vuetify.breakpoint.xs)" class="k-lesson-course-unit-header" v-html="course_unit_header"></div>
				<div v-if="!($vuetify.breakpoint.xs)" class="mr-2" v-html="date_header"></div>
			</div>

			<LessonView :lesson="item_to_show" :print_lesson_dom_id="print_lesson_dom_id" :show_print_dialog="show_print_dialog" @close_lesson="close_lesson"/>

			<k-card-actions class="px-1 pt-2 pb-0" :style="item_to_show.lp_variant=='A'?'border-top:1px solid #999;':''">
				<k-tooltip top><template v-slot:activator="{on}"><k-btn v-if="lp_context || publish_status_showing" v-show="$vuetify.breakpoint.smAndUp" v-on="on" class="ml-1 mr-1" fab x-small dark color="secondary" @click="shift_item_from_button"><k-icon small>fas {{item_shifted?'fa-angles-left':'fa-angles-right'}}</k-icon></k-btn></template>Shift Lesson</k-tooltip>
				<k-tooltip top><template v-slot:activator="{on}"><k-btn v-show="$vuetify.breakpoint.smAndUp" v-on="on" class="ml-1 mr-1" fab x-small dark color="#333" @click="print_lesson"><k-icon small>fas fa-print</k-icon></k-btn></template>Print Lesson</k-tooltip>
				<k-tooltip top><template v-slot:activator="{on}"><k-btn v-on="on" class="ml-1 mr-1" fab x-small dark color="#333" :href="standalone_cureum_link" target="_blank"><k-icon small>fas fa-arrow-up-right-from-square</k-icon></k-btn></template>Open Lesson in new window</k-tooltip>
				<k-tooltip top><template v-slot:activator="{on}"><k-btn v-on="on" class="ml-1 mr-1" fab x-small dark color="#333" @click="copy_link_to_clipboard"><k-icon small>fas fa-link</k-icon></k-btn></template>Copy Lesson link to clipboard</k-tooltip>
				<k-tooltip top><template v-slot:activator="{on}"><k-btn v-on="on" v-show="option_availability.report_issue" class="ml-1 mr-1" fab x-small dark color="#333" @click="report_issue"><k-icon small>fas fa-triangle-exclamation</k-icon></k-btn></template>Provide feedback about this Lesson Plan</k-tooltip>
				<k-tooltip top><template v-slot:activator="{on}"><k-btn v-on="on" v-show="option_availability.copy_item_for_my_use" @click="copy_to_my_content(false,'force_copy')" color="green darken-2" dark class="mx-1" fab x-small><k-icon small>fas fa-cubes-stacked</k-icon></k-btn></template>Copy Lesson Plan for my use</k-tooltip>
				<k-tooltip top><template v-slot:activator="{on}"><k-btn v-if="option_availability.publish_status" v-on="on" class="ml-1 mr-1" fab x-small dark :color="current_publish_status.color + ' darken-3'" @click="show_hqim_status"><k-icon small>fas {{current_publish_status.icon}}</k-icon></k-btn></template><div class="text-center">{{asset_manager_text.status}}:<br>{{current_publish_status.text}}</div></k-tooltip>
				<k-tooltip top><template v-slot:activator="{on}"><k-btn v-on="on" v-show="option_availability.share" class="ml-1 mr-1" fab x-small color="purple" dark @click="share_item('here')"><k-icon>fas fa-share-nodes</k-icon></k-btn></template>Share Lesson with other educators</k-tooltip>
				<k-tooltip top><template v-slot:activator="{on}"><k-btn v-on="on" v-show="option_availability.import_to_district" class="ml-1 mr-1" fab x-small color="indigo" dark @click="share_item(option_availability.import_to_district)"><k-icon>fas fa-share-nodes</k-icon></k-btn></template>Import Lesson to {{ option_availability.import_to_district }}</k-tooltip>
				<k-tooltip top><template v-slot:activator="{on}"><k-btn v-show="option_availability.edit && $vuetify.breakpoint.smAndUp" v-on="on" class="ml-1 mr-1" fab x-small dark color="blue darken-2" @click="edit_item_start"><k-icon small>fas fa-edit</k-icon></k-btn></template>Edit Lesson</k-tooltip>
				<k-tooltip top><template v-slot:activator="{on}"><k-btn v-show="option_availability.remove && $vuetify.breakpoint.smAndUp" v-on="on" class="ml-1 mr-1" fab x-small dark color="red darken-2" @click="$emit('remove_item', item)"><k-icon small>fas fa-trash-alt</k-icon></k-btn></template>Remove Lesson</k-tooltip>

				<k-spacer/>
				<div v-if="item_copy_for_user && !force_show_original" style="font-size:12px">
					<k-icon color="green darken-2" small class="mr-1">fas fa-cubes-stacked</k-icon>
					<i>This lesson has been copied for your use.</i>
					<k-btn x-small outlined class="ml-1" color="green darken-2" @click="show_original">Show Original</k-btn>
				</div>
				<div v-if="force_show_original" style="font-size:12px">
					<k-icon color="green darken-2" small class="mr-1">fas fa-cubes-stacked</k-icon>
					<i>You are viewing the original version of a lesson you copied for your use</i>
					<k-btn x-small outlined class="ml-1" color="green darken-2" @click="show_my_copy">Show My Copy</k-btn>
				</div>
				<k-spacer/>
				<k-btn color="secondary" dark @click="close_lesson"><k-icon small class="mr-2">fas fa-times</k-icon>Done</k-btn>
			</k-card-actions>
		</k-card>
	</k-dialog>

	<LessonPrint v-if="show_print_dialog" :lesson="item_to_show" :print_lesson_dom_id="print_lesson_dom_id" @dialog_cancel="show_print_dialog=false" />

	<k-dialog
		v-if="lesson_being_edited"
		v-model="lesson_editor_showing"
		:max-width="use_enhanced_lesson_editor ? 1200 : 900"
		:hide-overlay="item_shifted"
		:no-click-animation="item_shifted"
		:retain-focus="!item_shifted"
		:content-class="`k-resource-collection-item-lesson-card-dialog k-lpe-editor-dialog ${item_shifted && !quick_look_shifted ? 'k-resource-collection-lesson-item-card-dialog-shifted' : ''}`"
		persistent
		scrollable
	>
		<k-card class="k-resource-collection-item-lesson-card">
			<div class="d-flex">
				<k-icon class="mr-2" style="margin-top:-2px;">fas fa-rectangle-list</k-icon>
				<div class="k-lesson-title"><b style="font-weight:900" :style="lesson_editor_title_style" v-html="item_title"></b></div>
				<k-spacer/>

				<div v-if="!show_lesson_variant_switcher && use_enhanced_lesson_editor" style="cursor:pointer; white-space:nowrap; font-size:15px;" @click="show_llm_lesson_plan_description"><nobr><k-icon color="light-blue" class="mr-2">fas fa-info-circle</k-icon>Lesson Plan Companion<k-icon class="k-lpe-dog-icon mx-2" style="margin-top:-5px">fas fa-dog</k-icon><!-- (<b class="red--text text--darken-3">BETA</b>)--></nobr></div>
				<k-checkbox v-if="show_lesson_variant_switcher" class="mt-0 pt-0" v-model="using_llm" hide-details off-icon="far fa-square" on-icon="fas fa-check-square"><template v-slot:label><nobr><span style="color:#444">Use Lesson Plan Companion <k-icon class="k-lpe-dog-icon" style="margin-top:-5px">fas fa-dog</k-icon><!-- (<b class="red--text text--darken-3">BETA</b>)--></span></nobr></template></k-checkbox>
			</div>

			<div v-if="use_enhanced_lesson_editor" class="k-lpe-lesson-edit-outer">
				<LessonEditorEnhanced ref="lesson_editor_component" :original_lesson="lesson_being_edited" :original_lp_variant="original_edited_lesson_lp_variant" :lesson_class="lesson_being_edited_lesson_class" :course_code="course_code" :lp_unit_id="lp_unit_id" :lesson_shifted="item_shifted" @shift_lesson="shift_item_from_button" @edit_lesson_cancel="edit_lesson_cancel" @edit_lesson_saved="edit_lesson_saved" />
			</div>

			<div v-else class="k-lesson-edit-outer">
				<LessonEditor ref="lesson_editor_component" :original_lesson="lesson_being_edited" :lesson_class="lesson_being_edited_lesson_class" :course_code="course_code" :lp_unit_id="lp_unit_id" :lesson_shifted="item_shifted" @shift_lesson="shift_item_from_button" @edit_lesson_cancel="edit_lesson_cancel" @edit_lesson_saved="edit_lesson_saved" />
			</div>
		</k-card>
	</k-dialog>

	<!-- Resource view dialog, shown when the user clicks "View Resource Side-by-Side" from Quick Look -->
	<k-dialog
		v-if="resource_showing"
		v-show="!resource_being_edited"
		v-model="resource_showing"
		max-width="900"
		:hide-overlay="item_shifted"
		:no-click-animation="item_shifted"
		:retain-focus="!item_shifted"
		:content-class="`k-resource-collection-item-resource-view-card-dialog ${item_shifted && !quick_look_shifted ? 'k-resource-collection-lesson-item-card-dialog-shifted' : ''}`"
		:persistent="item_shifted"
		scrollable
	>
		<k-card class="k-resource-collection-item-lesson-card">
			<div class="d-flex align-center pb-2" style="border-bottom:1px solid #999">
				<k-icon class="mr-2" :color="color_for_asset_type">fas {{icon}}</k-icon>
				<div class="k-lesson-title" style="font-weight:900" v-html="item_title" tabindex="0" data-k-dialog-initial-focus="true"></div>
				<k-spacer/>
				<k-tooltip bottom><template v-slot:activator="{on}"><k-btn v-show="option_availability.edit" v-on="on" class="ml-3" fab x-small dark color="blue darken-2" @click="edit_item_start()"><k-icon small>fas fa-edit</k-icon></k-btn></template>Edit Resource</k-tooltip>
				<k-btn color="secondary" dark class="ml-3" @click="close_resource_view"><k-icon small class="mr-2">fas fa-times</k-icon>Done</k-btn>
			</div>

			<div class="mt-3 mx-2 k-aspect-ratio-box--10-8 k-resource-link-iframe-outer"><div class="k-aspect-ratio-box-inside">
				<div v-if="resource_is_image" style="overflow:auto; height:100%"><img :src="inline_document_src" :alt="item_to_show.description" style="max-width:100%; max-height:calc(100% - 8px);"></div>
				<iframe v-else-if="resource_is_youtube" width="100%" height="100%" :src="youtube_embed_url" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>
				<iframe v-else-if="resource_iframeable" :src="inline_document_src" class="k-resource-link-iframe"></iframe>
				<div v-else class="text-center pa-6">
					<div class="mb-3">This resource can’t be shown inline.</div>
					<k-btn color="primary" :href="standalone_cureum_link" target="_blank"><k-icon small class="mr-2">fas fa-arrow-up-right-from-square</k-icon>Open Resource in New Window</k-btn>
				</div>
			</div></div>
		</k-card>
	</k-dialog>

	<!-- Quick Look dialog -->
	<k-dialog 
		v-if="resource_quick_look_showing"
		v-model="resource_quick_look_showing"
		:persistent="in_reslink_view || item_shifted"
		:max-width="quick_look_max_width"
		:hide-overlay="in_reslink_view || !!(lp_context && lp_context.shifted_for_item)"
		:no-click-animation="!!(lp_context && lp_context.shifted_for_item)"
		:content-class="quick_look_content_class"
		scrollable
		@input="item_shifted ? null : hide_quick_look()"
	> <!-- note that the @input handler runs when the user clicks outside the dialog, closing it -->
		<k-card class="k-resource-collection-item-lesson-card px-0 mt-0 pb-4" style="overflow: auto;">
			<div class="d-flex mb-1 mx-2 align-center">
				<k-icon class="mr-2 ml-1" :color="color_for_asset_type">fas {{icon}}</k-icon>
				<div class="k-lesson-title" style="font-weight:bold; line-height:24px;" v-html="item_to_show.title()" tabindex="0" data-k-dialog-initial-focus="true"></div>
				<k-spacer/>

				<div v-if="show_new_indicator" class="k-new-indicator-quick-look d-flex mr-2">NEW <k-icon color="white" class="ml-1" x-small>fas fa-sparkles</k-icon></div>

				<!-- open in new window, shown only if HQIM interface is visible -->
				<k-tooltip bottom><template v-slot:activator="{on}"><k-btn v-if="!lti_chooser&&publish_status_showing" v-on="on" class="ml-3" fab x-small dark color="primary" @click="card_clicked('from_quick_look')"><k-icon small>fas fa-arrow-up-right-from-square</k-icon></k-btn></template>Open {{is_lesson?'Lesson':is_sparkl?'Activity':'Resource'}} in new window</k-tooltip>

				<k-tooltip bottom><template v-slot:activator="{on}"><k-btn v-show="!lti_chooser && option_availability.edit && !force_show_original && !item_shifted && !publish_status_showing" v-on="on" class="ml-3" fab x-small dark color="blue darken-2" @click="edit_item_start()"><k-icon small>fas fa-edit</k-icon></k-btn></template>Edit {{is_lesson ? 'Lesson' : is_sparkl ? 'Activity' : 'Resource'}}</k-tooltip>
				<k-tooltip bottom><template v-slot:activator="{on}"><k-btn v-show="show_shift_item_button" v-on="on" class="ml-3" fab x-small dark color="blue darken-2" @click="open_item_shifted"><k-icon small>fas fa-table-columns</k-icon></k-btn></template>View {{is_lesson ? 'Lesson' : 'Resource'}} Side-by-Side</k-tooltip>
				<!-- hide unpublish btn when in quick_look_only mode, as that mode is currently used when showing a resource... -->
				<k-tooltip bottom><template v-slot:activator="{on}"><k-btn v-if="option_availability.publish_status && !is_lesson && !quick_look_only" v-on="on" class="ml-3" fab x-small dark :color="current_publish_status.color + ' darken-3'" @click="publish_status_showing=!publish_status_showing"><k-icon small>fas {{current_publish_status.icon}}</k-icon></k-btn></template><div class="text-center">{{asset_manager_text.status}}:<br>{{current_publish_status.text}}</div></k-tooltip>
				<k-tooltip bottom><template v-slot:activator="{on}"><k-btn v-if="!lti_chooser && option_availability.unpublish && !quick_look_only" v-on="on" class="ml-3" fab x-small dark color="red darken-2" @click="unpublish_item"><k-icon small>fas fa-ban</k-icon></k-btn></template>“Unpublish” resource</k-tooltip>
				<k-tooltip bottom><template v-slot:activator="{on}"><k-btn v-if="!lti_chooser && option_availability.pin_item" v-on="on" class="ml-3" fab x-small dark color="pink darken-2" @click="pin_item('toggle')"><k-icon small>fas fa-thumbtack</k-icon></k-btn></template>“Pin” resource</k-tooltip>

				<k-tooltip bottom><template v-slot:activator="{on}"><k-btn v-if="!lti_chooser" v-on="on" class="ml-3" fab x-small dark color="#333" @click="copy_link_to_clipboard"><k-icon small>fas fa-link</k-icon></k-btn></template>Copy resource link to clipboard</k-tooltip>
				<k-tooltip bottom><template v-slot:activator="{on}"><k-btn v-if="!lti_chooser && resource_iframeable" v-show="$vuetify.breakpoint.smAndUp" v-on="on" class="ml-3" fab x-small dark color="#333" @click="print_resource"><k-icon small>fas fa-print</k-icon></k-btn></template>Print resource</k-tooltip>

				<k-tooltip v-show="item_shifted" bottom><template v-slot:activator="{on}"><k-btn v-show="item_shifted" v-on="on" class="ml-3" fab x-small dark color="secondary" @click="toggle_quick_look_shift"><k-icon small>fas {{quick_look_shifted ? 'fa-angles-left' : 'fa-angles-right'}}</k-icon></k-btn></template>Shift Quick Look</k-tooltip>
				<k-btn
					v-if="!in_reslink_view"
					color="secondary"
					class="ml-3"
					dark
					@click="hide_quick_look"
				>
					<k-icon small class="mr-2">fas fa-times</k-icon>Done
				</k-btn>
			</div>

			<k-card-text :class="(quick_look_shifted||!item_shifted) ? 'px-6' : 'px-3'">
				<!-- PW 7/16/2026: added open in new window btn to top when HQIM showing, so I don't think we need to make this area scrollable anymore `max-height: 90%; overflow: auto;` -->
				<div v-if="publish_status_showing" class="mt-3" style="border: 1px solid lightgray; border-radius: 12px;">
					<AssetPublishStatus :item="item_to_show" :publish_role_override="unpublished_display" :disable_submit="!!lesson_being_edited" @status_updated="status_updated"/>
				</div>

				<!-- alignments -->
				<div v-if="item_to_show.standards && item_to_show.standards.length>0" class="d-flex flex-wrap align-center mt-3 mb-1" style="justify-content:center">
					<span style="font-size:16px; color:#333;" class="mr-1">Alignments:</span>
					<CASEItemBtn v-for="(s, i) in item_to_show.standards" :key="s.identifier" small :btn_color="s.btn_color(item_to_show.standards)" :outer_class="'ma-1'" :item="s" :selected_items="item_to_show.standards" :framework_identifier="s.framework_identifier" satchel_determines_framework_id="yes" xstar_primary_alignments="yes" />
				</div>

				<div v-if="item_to_show.long_description" class="k-resource-collection-item-long-description mt-3 mx-auto" v-html="U.render_latex(item_to_show.long_description)"></div>

				<div v-if="!lti_chooser" class="text-center mt-3 mb-2">
					<!-- button to open resource, shown regardless of whether or not we're showing in an iframe -->
					<k-tooltip :disabled="is_sparkl || !site_config.open_resource_tooltip_content" bottom>
						<template v-slot:activator="{on}">
							<!-- TODO: make this the first button that gets focus -->
							<k-btn v-on="on" class="k-tight-btn" color="primary" @click="card_clicked('from_quick_look')">
								Open {{ item_noun(true) }} {{is_sparkl ? '' : 'in New Window'}}
								<k-icon class="ml-2" small>fas fa-arrow-up-right-from-square</k-icon></k-btn>
						</template>
						<span v-html="site_config.open_resource_tooltip_content"></span>
					</k-tooltip>

					<!-- button to add to google classroom -->
					<k-btn class="k-tight-btn ml-2" color="primary" 
						v-if="option_availability.add_to_gc"
						@click="add_to_gc"
					>
						<img src="./../../images/google_classroom_logo.png" alt="" aria-hidden="true" style="height: 18px; margin-top:0px; margin-right:8px; border-radius: 3px;">
						Add To Google Classroom
					</k-btn>

					<!-- button to create a sparkl with the resource, or assign the sparkl activity -->
					<k-tooltip bottom><template v-slot:activator="{on}"><k-btn v-on="on" v-if="option_availability.create_sparkl_from_resource && item_type!='sparkl'" class="ml-2 k-tight-btn" dark color="green darken-3" @click="create_activity_from_resource"><k-icon class="mr-1" small>fas fa-star</k-icon>Create or Add to Student Activity</k-btn></template>Create a new Student Activity with this resource, or add it to an existing Activity</k-tooltip>
					
					<k-tooltip bottom><template v-slot:activator="{on}"><k-btn v-on="on" v-if="option_availability.create_sparkl_from_resource && item_type=='sparkl'" class="ml-2 k-tight-btn" dark color="green darken-3" @click="create_activity_from_resource"><k-icon class="mr-1" small>fas fa-star</k-icon>Assign This Activity</k-btn></template>Assign this activity to your student</k-tooltip>
				</div>

				<!-- lti_chooser buttons -->
				<div v-if="lti_chooser" class="text-center mt-3 mb-2">
					<k-btn dark color="#444" class="mr-3" @click="lti_chooser_preview"><k-icon small class="mr-2">fas fa-glasses</k-icon> Preview {{item_noun(true)}}</k-btn>
					<k-btn dark color="indigo darken-2" @click="lti_chooser_choose">Choose {{ item_noun(true) }} <k-icon class="ml-2">fas fa-circle-check</k-icon></k-btn>
				</div>

				<div v-if="show_inline_document" class="mt-3 mx-2 k-aspect-ratio-box--10-8 k-resource-link-iframe-outer"><div class="k-aspect-ratio-box-inside">
					<div v-if="resource_is_image" style="overflow:auto; height:100%"><img :src="inline_document_src" :alt="item_to_show.description || item_noun(true)" style="max-width:100%; max-height:calc(100% - 8px);"></div>
					<iframe v-if="resource_is_youtube" width="100%" height="100%" :src="youtube_embed_url" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>
					<iframe v-else :id="'inline_iframe_'+item_to_show.resource_id" :src="inline_document_src" class="k-resource-link-iframe"></iframe>
				</div></div>

				<div v-if="show_inline_html" class="mt-3 mx-2 k-aspect-ratio-box--10-8 k-resource-link-iframe-outer" style="border:1px solid #999; border-radius:10px"><div class="k-aspect-ratio-box-inside">
					<iframe :name="'inline_iframe_'+item_to_show.resource_id" class="k-resource-link-iframe"></iframe>
				</div></div>

				<div v-if="site_config.show_resource_ratings && !publish_status_showing" class="d-flex mt-2 mb-1">
					<k-spacer/>
					<div class="d-flex align-center py-2 px-3" style="font-size:16px; background-color:#eee; border-radius:6px;">
						<div class="mr-4">What do you think of this {{ item_noun() }}?</div>
						<k-tooltip bottom><template v-slot:activator="{on}"><k-icon v-on="on" @click="rate_resource(1)" color="amber darken-3" style="font-size:34px">fas fa-fw fa-face-meh</k-icon></template>Meh</k-tooltip>
						<k-tooltip bottom><template v-slot:activator="{on}"><k-icon v-on="on" @click="rate_resource(2)" color="light-blue darken-2" style="font-size:34px" class="mx-2">fas fa-fw fa-face-smile</k-icon></template>Pretty good</k-tooltip>
						<k-tooltip bottom><template v-slot:activator="{on}"><k-icon v-on="on" @click="rate_resource(3)" color="green darken-3" style="font-size:34px">fas fa-fw fa-face-smile-hearts</k-icon></template>Awesome</k-tooltip>
						<k-tooltip bottom><template v-slot:activator="{on}"><k-icon v-on="on" @click="report_issue" color="red darken-3" style="font-size:34px" class="ml-10">fas fa-fw fa-triangle-exclamation</k-icon></template>Report a problem</k-tooltip>
					</div>
					<k-spacer/>
				</div>

				<div v-if="resource_extensions" class="k-resource-collection-item-extensions mt-3 mx-2" v-html="resource_extensions"></div>
				<div v-if="!is_temp_resource" class="text-right mt-2 mr-2" style="font-size:10px; color:#999; cursor:pointer" @click="copy_reslink_to_clipboard"><b>Resource ID:</b> {{item_id}}<k-icon x-small class="ml-1" color="#999">fas fa-link</k-icon></div>

				<div v-if="site_config.not_agency_sanctioned_warning_resource && !item_to_show.agency_sanctioned" class="mt-3 pt-3 px-5" style="border-top:1px solid #ccc; font-size:14px; line-height:18px" v-html="site_config.not_agency_sanctioned_warning_resource"></div>
			</k-card-text>
		</k-card>
	</k-dialog>

	<VideoPlayer v-if="show_video_player" :resource="item" @dialog_cancel="show_video_player=false" @video_complete="video_complete" @save_video_progress="save_video_progress"/>

	<AssessmentOptions v-if="show_assessment_options" :resource="item" 
		@open_lti_assessment="get_resource_record('launch')"
		@add_to_gc="add_to_gc"
		@dialog_cancel="show_assessment_options = false"
	/>

	<ResourceEditor
		v-if="resource_being_edited"
		:original_resource="resource_being_edited"
		:lesson="lesson_context"
		:unit="unit_context"
		:course="lp_context"
		:shifted="item_shifted"
		@shift_resource="shift_item_from_button"
		@edit_resource_cancel="edit_resource_cancel"
		@edit_resource_saved="edit_resource_saved"
	/>
</div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import LessonView from '../lessons/LessonView'
import LessonPrint from '../lessons/LessonPrint'
import LessonEditor from '../lessons/LessonEditor'
import LessonEditorEnhanced from '../lessons/LessonEditorEnhanced'
import ResourceEditor from '../resources/ResourceEditor'
import VideoPlayer from './VideoPlayer'
import AssessmentOptions from './AssessmentOptions'
import CASEItemBtn from '../standards/CASEItemBtn'
import AssetPublishStatus from '../assetManager/AssetPublishStatus'

export default {
	name: 'ResourceCollectionItem',

	components: { LessonView, LessonPrint, LessonEditor, LessonEditorEnhanced, ResourceEditor, VideoPlayer, AssessmentOptions, CASEItemBtn, AssetPublishStatus },

	props: {
		item: { type: Object, required: true },
		large: { type: Boolean, required: false, default() { return false }},
		lesson_context: { type: Object, required: false, default() { return null } },
		unit_context: { type: Object, required: false, default() { return null } },
		lp_context: { type: Object, required: false, default() { return null } },

		// this allows the includer to override option_availability settings
		option_overrides: { type: Object, required: false, default() { return {} } },

		// this allows
		title_override: { type: String, required: false, default() { return '' } },

		// if this is true we show as a row in a list
		full_width_resource: { type: Boolean, required: false, default() { return false } },

		// if this is true we always/only show the quick look
		quick_look_only: { type: Boolean, required: false, default() { return false } },

		// if this is true we open with the "publish_status" element showing
		open_to_publish_status: { type: Boolean, required: false, default() { return false } },

		// not currently using the tcc checkboxes on individual resources; only folders
		// show_checkbox: { type: Boolean, required: false, default() { return false } },
		// checkbox_initially_checked: { type: Boolean, required: false, default() { return false } },

		from_pd_resource_popup_collection: { type: Boolean, required: false, default() { return false } },

		from_archived: { type: Boolean, required: false, default() { return false } },

		show_unpublished: { type: Boolean, required: false, default: false }
	},

	data() { return {
		in_unit_editor: false,
		in_lesson_editor: false,
		lesson_showing: false,
		resource_showing: false,
		resource_quick_look_showing: false,
		show_inline_document: false,
		show_inline_html: false,
		inline_html_always: 'Loading...',
		inline_html_extra: '',
		show_inline_html_extra: false,
		show_print_dialog: false,
		print_lesson_dom_id: U.new_uuid(),
		item_shifted: false,
		quick_look_shifted: false,
		quick_look_original_z_index: null,

		publish_status_showing: false,

		// note that force_show_original is implemented differently for lessons and sparkl activities
		force_show_original: false,

		lesson_being_edited: null,
		lesson_editor_showing: false,
		lesson_being_edited_lesson_class: '',
		original_edited_lesson_lp_variant: '',
		resource_being_edited: null,
		using_llm: false,

		// these are for sparkl; see show_sparkl
		force_allow_original_to_be_edited: 'no',
		force_prevent_original_to_be_edited: 'no',
		sparkl_closed_from_embed: false,

		link_clicked: false,
		show_video_player: false,
		show_assessment_options: false,

		default_max_standards_showing: 1,
		show_all_standards: false,

		// not currently using the tcc checkboxes on individual resources; only folders
		// checkbox_checked: false,
	}},

	computed: {
		...mapState(['user_info', 'user_is_touching', 'my_lessons', 'my_resources', 'my_ca_mappings', 'my_archived_lessons', 'my_archived_resources', 'site_config', 'all_courses', 'all_courses_loaded']),
		...mapGetters(['signed_in', 'studentish_role', 'my_default_collection', 'manage_assignments', 'lti_chooser']),

		in_reslink_view() { return vapp.in_reslink_view },
		reslink_embed_mode() { return vapp.reslink_embed_mode },

		course_code() { return this.lp_context ? this.lp_context.course_code : '' },

		lp_unit_id() { return this.unit_context ? this.unit_context.lp_unit_id : 0 },

		viewing_my_default_collection() { return this.lp_context && this.lp_context == this.my_default_collection },

		viewing_my_shadow_unit() { return this.unit_context && this.unit_context.shadows_lp_unit_id != 0 && this.unit_context.shadow_unit_owner_id == this.user_info.user_id },

		collection_type() { return this.lp_context ? this.lp_context.collection_type : '?' },

		// determine whether or not to allow the user to toggle between the LPC and the "OG" lesson editor
		show_lesson_variant_switcher() { 
			// we don't allow switching from variant B back to variant A
			if (this.original_edited_lesson_lp_variant == 'B') return false

			// else use what's calculated in U.show_lesson_variant_switcher
			return U.show_lesson_variant_switcher(this.lesson_being_edited.lesson_id, this.lp_context?.course_code) 
		},
		
		// now separately, determine whether or not to *use* the LPC or the OG lesson editor
		use_enhanced_lesson_editor() { return this.lesson_being_edited.lp_variant=='B' },

		user_is_admin() {
			return vapp.has_admin_right('su')
		},

		is_lesson() { return !empty(this.item.lesson_title) },

		// Note that sparkl USED TO be deployed as either a resource (for a DOE-provided activity), or as an "activity" object (a user's copy of the activity); now sparkls are always resources
		is_resource() { return !this.is_lesson },

		is_sparkl() { return this.item.type == 'sparkl' },

		is_sparkl_bank() { return this.item.type == 'sparkl_bank' },

		is_non_sparkl_resource() { return this.is_resource && !this.is_sparkl && !this.is_sparkl_bank },

		// whether to show the "View Lesson/Resource Side-by-Side" button in Quick Look; viewing doesn't require edit rights (only the Edit button inside the shifted view does)
		show_shift_item_button() {
			if (this.item_shifted) return false
			if (!this.publish_status_showing) return false
			return this.is_lesson || this.is_non_sparkl_resource
		},

		is_temp_resource() {
			return /^TEMP-/.test(this.item.resource_id)
		},

		lti_activity_id() {
			// for sparkl activities, we link lti or gc attachments to the sparkl activity_id, which is in resource.url
			if (this.item_type == 'sparkl') {
				return this.item_to_show.url
			
			// else we use this form, which tells LTI or the Sparkl add-on where to redirect
			} else if (this.item_type == 'lesson') {
				return `${this.$store.getters.cureum_code}--${this.item_to_show.lesson_id}`
			} else {
				return `${this.$store.getters.cureum_code}--${this.item_to_show.resource_id}`
			}
		},
		lti_activity_title() {
			if (this.item_type == 'lesson') {
				return this.item_to_show.lesson_title
			} else {
				return this.item_to_show.description
			}
		},

		// find the ancestor CollectionUnit component where this item was launched from (might be null)
		collection_unit_component() { return U.find_ancestor_vue_component(this, 'CollectionUnit') },

		// this fn checks to see if the user has a copy of the specified item, and returns the copy if so.
		// item_to_show (below) then determines whether to show the original or the copy (if a copy exists)
		// for now at least, we only actually check to see if the lesson/activity is in your default collection or your shadow collection; we don't look in other 'my' collections that you have access to
		item_copy_for_user() {
			// if user isn't signed in, they can't have a copy
			if (!this.signed_in) return null

			// in the following situations we never want to "overwrite" with a copy:

			// if we're viewing the default collection, and we're not in the lesson editor
			if (this.viewing_my_default_collection && !this.in_lesson_editor) return null
			// if we're viewing a shadow unit for the user
			if (this.viewing_my_shadow_unit) return null
			// if we're viewing an item in a non-agency-sanctioned collection, and we're not in the lesson editor
			if (this.lp_context && !this.lp_context.agency_sanctioned && !this.in_lesson_editor) return null
			// if we're in the unit editor
			// if (this.in_unit_editor) return null

			// if we're in the reslink view
			if (this.in_reslink_view) return null
			
			// if we're viewing an item in a non-agency-sanctioned collection and the user is the owner of the collection [replaced with more general clause above]
			// if (this.lp_context && !this.lp_context.agency_sanctioned && this.lp_context.owner_id == this.user_info.user_id) return null

			// for lessons and sparkls, look first in my_x, then in my_default_collection
			if (this.is_lesson) {
				let o = this.my_lessons.find(x=>x.lesson_template_id == this.item.lesson_id)
				if (o) return o

				if (this.my_default_collection) {
					o = this.my_default_collection.units[0].lessons.find(x=>x.lesson_template_id == this.item.lesson_id)
					if (o) return o
				}

				return null
			} else if (this.is_sparkl) {
				let o = this.my_resources.find(x=>x.resource_template_id == this.item.resource_id)
				if (o) return o

				if (this.my_default_collection) {
					o = this.my_default_collection.units[1].resources.find(x=>x.resource_template_id == this.item.resource_id)
					if (o) return o
				}

				return null
			} else {
				// for link resources and sparkl_banks, we don't actually make "deep copies" for users
				return null
			}
		},

		// if the item is a lesson or activity you have a copy of, we want to show/edit your copy in many (but not all) circumstances
		item_to_show() {
			// if we're in the context of the unit editor, always show the original item
			if (this.in_unit_editor) return this.item

			// if force_show_original is true, always use the original item
			if (this.force_show_original) return this.item

			// if we get to here and we have an item_copy_for_user, show it
			return this.item_copy_for_user ?? this.item
		},

		option_availability() {
			let o = {}

			// console.warn(`${this.item_title} -- calculating option_availability`, this.option_overrides)

			// general note below: some things are never doable if we're in a context where we don't have a my_default_collection for the user

			// open in new window: just lessons (at least for now) -- for most items you just click "open" to open in a new window
			o.open_in_new_window = this.is_lesson
			
			// students/parents can only do the things above here
			if (this.studentish_role) return o

			// import_to_district: signed in and email domain is in site_config.import_to_district_domains list
			// also, we currently don't support this for non-sparkl resources
			o.import_to_district = false
			if (!this.is_non_sparkl_resource) {
				let email_domain = ''
				if (this.signed_in) email_domain = this.user_info.email.replace(/.*@/, '').toLowerCase()
				// import_to_district_domains will be an object; if the email_domain is a key, the value will be the name of the district's cureum app
				o.import_to_district = this.site_config.import_to_district_domains[email_domain]
			}

			// edit: if you're signed in and not in the archived collection
			o.edit = false
			o.remove = false
			if (this.signed_in && !this.from_archived) {
				// you can always remove from your default collection, a shadow unit, or in the context of the unit or lesson editor
				// if you're viewing your default collection, a shadow unit, or are in the context of the unit or lesson editor,
				if (this.viewing_my_default_collection || this.viewing_my_shadow_unit || this.in_unit_editor || this.in_lesson_editor) {
					// you can remove
					o.remove = true
					// and we'll assume for now you can edit
					o.edit = true
				}

				// if you are the creator of the item you can edit
				if (this.item_created_by_this_user) o.edit = true
				
				// for now we'll assume that if you are an editor of this collection and we aren't in a lesson context you can edit the item
				if (this.lp_context && this.lp_context.user_is_lp_admin() && !this.lesson_context) o.edit = true

				// BUT, if you choose to edit and the item has a home_collection_id, we'll check for your editing rights to the home_collection_id
				// (we may not know about the home_collection_id at this time, which is why we don't check now)
			}

			// Once an asset enters the publishing pipeline, revoke edit access unless the user is an LP admin or superuser
			const pipeline_statuses = ['submitted', 'under_review', 'ready_for_publication', 'published']
			if (pipeline_statuses.includes(this.item_to_show.publish_status) && o.edit) {
				o.edit = !!(this.lp_context?.user_is_lp_admin() || this.user_is_admin)
			}

			// you can always remove items from lessons if you're editing the lesson
			if (this.in_lesson_editor) o.remove = true

			o.show_original = (this.item_to_show && this.item_to_show != this.item)

			// share with other users: signed in; the item is not archived [MC]; not for banks at this time; not for temp resources
			o.share = (this.signed_in && !this.from_archived && !this.is_sparkl_bank && !this.is_temp_resource)
			// allow for an extensions override
			if (this.item_to_show.extensions?.no_option_availability_share === true) o.share = false

			// copy the item for your use: if this is an agency-sanctioned collection and it isn't a non-sparkl resource, this makes a copy of the lesson/sparkl in your my content area
			// if this is a my collection (or your sandbox), this shows you the share code with instructions for how to make a copy somewhere else
			// sparkl_banks can't be copied for your use
			// also note: we NEVER show this option in the kebab menu; it is accessed from the LPC for lessons or from sparklembed for sparkls
			o.copy_item_for_my_use = false
			if (this.signed_in && !this.from_archived && !this.is_sparkl_bank && this.my_default_collection) {
				// for non-sparkl resources, allow for this if the user didn't already copy the item to the default collection
				if (this.is_non_sparkl_resource) {
					o.copy_item_for_my_use = !empty(this.my_default_collection.units[2].resources.find(x=>x.resource_id == this.item.resource_id))
				
				// for lessons and sparkls, we allow for it if the user hasn't already made a copy for their use and they're not in the unit editor
				} else {
					if (this.item_copy_for_user == null && !this.in_unit_editor) {
						// note, however, that SparklEmbed may "overrule" this (see show_copy_for_my_use_btn)
						o.copy_item_for_my_use = true
					}
				}
			}

			// For now at least, if import_to_district is true (which will be the case if this is GA inspire and the user is in Henry County), don't show the copy_item_for_my_use option
			if (o.import_to_district) o.copy_item_for_my_use = false

			// TODO: roles -- for many of the things here??

			// if you're signed in and this is a non-sparkl, non-sparkl_bank resource, offer to create an activity from it
			// currently this is limited to situations where the resource collection item is showing in the context of a unit where the assignments tab is showing
			o.create_sparkl_from_resource = this.signed_in && this.is_non_sparkl_resource && this.collection_unit_component && this.collection_unit_component.show_assignments_tab	//  && !this.item.teacher_facing

			// add to gc: if site config says so; if we're not in a standalone resource window; and if a sparkl or non-sparkl resources (it doesn't make sense to add lessons to GC)
			o.add_to_gc = this.site_config.enable_add_to_gc_in_kebab == 'yes' && !this.in_reslink_view && (this.is_non_sparkl_resource || this.is_sparkl)
			// allow for an extensions override
			if (this.item_to_show.extensions?.no_option_availability_add_to_gc === true) o.add_to_gc = false

			// open in heyc: only for non-sparkl resources that don't use LTI, and when site_config.enable_heyc_connection is on
			o.open_in_heyc = (this.is_non_sparkl_resource && this.item_type != 'lti' && this.site_config.enable_heyc_connection)

			// report issue/feedback: signed in, site_config.show_submit_feedback_option_for_resources 'yes'
			// PW 6/23/2026: we used also check for ` && this.item.agency_sanctioned`, but GADOE wants users to be able to report problems with LPC items, and why not allow people to report an issue with any item??
			o.report_issue = (this.signed_in && this.site_config.show_submit_feedback_option_for_resources == 'yes')

			// show_restricted_resources_control: site_config setting is on and in the unit editor (which implies that you also have edit access).
			// we don't just check o.edit here because for 'my' collections, we *don't* want to show the edit restriction option, or show the "unlocked" icon in the menu, unless you're in the unit editor
			o.show_restricted_resources_control = this.site_config.show_restricted_resources_control == 'yes' && this.in_unit_editor

			// currently, at least, if we're in a unit folder (not a lesson) and you can edit the item and you're in the unit editor, 
			// you can duplicate the item, if it's a lesson or sparkl activity (not a bank at this time)
			// EXCEPTION: If the item has been 'copied for my use', we do not allow duplication unless user is in the 'My Content' view
			o.duplicate = (o.edit && this.in_unit_editor && !this.item_copy_for_user && this.unit_context && (this.is_lesson || this.is_sparkl || this.is_non_sparkl_resource))
			if (o.duplicate && this.is_non_sparkl_resource) {
				// however, for non-sparkl resources, disallow for certain types that would also require to copy things besides the resource record itself
				if (['upload', 'html'].includes(this.item_to_show.type)) o.duplicate = false
			}

			// (important to do this check right here in the sequence): if the user is a superuser, and if the appropriate config value is set,
			// let them edit anything from anywhere (and unpublish if the other criteria below are met; but not necessarily remove)
			if (this.site_config.allow_superusers_to_edit_from_anywhere == 'yes' && this.user_is_admin && this.my_default_collection) o.edit = true

			// show the "add to open lesson" option if...
			o.add_to_lesson = false
			// we are in the context of a collection
			if (this.lp_context) {
				// and there is a lesson that is open, shifted to the right, and being edited
				if (this.lp_context.shifted_for_item && this.lp_context.shifted_for_item.lesson_being_edited) {
					o.add_to_lesson = true

					// and if this option is on, *don't* show some of the other options
					o.edit = false
					o.remove = false
					o.show_original = false
					o.duplicate = false		// this should already be false, but add here just in case
				}
			}

			// "unpublish" a resource: show if 0) o.edit, a) site_config.unpublished_items_collection is set, b) the user is a superuser, and c) we're *not* in the context of a my collection or a lesson
			o.unpublish = (o.edit && this.site_config.unpublished_items_collection && this.user_is_admin && this.collection_type != 'my' && !this.viewing_my_default_collection && !this.lesson_context)

			// if the user can is an lp_admin, and the site_config setting is enabled, they can pin
			// TODO: we probablyl need more controls than this...
			// o.pin_item = this.site_config.enable_resource_pinning && this.lp_context?.user_is_lp_admin()
			o.pin_item = this.site_config.enable_resource_pinning && o.edit

			// allow to edit publish status if...
			o.publish_status = false
			// site_config.asset_manager.enabled is true AND ...
			if (this.site_config.asset_manager.enabled) {
				// ... the user is a superuser
				if (this.user_is_admin) { 
					o.publish_status = true

				// OR asset_manager.collection_editors_only is false OR the user is an editor for the current collection AND ...
				} else if (this.site_config.asset_manager.collection_editors_only == false || (this.lp_context && this.lp_context.user_is_lp_admin() && !this.lesson_context)) {
				
					// ... (the asset isn't already published or we explicitly opened to the publish status interface) AND ...
					if (this.item_to_show.publish_status != 'published' || this.open_to_publish_status) {
						// ... the user is the item creator
						if (this.item_created_by_this_user) {
							o.publish_status = true

						// ... OR the item has no creator id AND ...
						} else if (this.item_creator_id == 0) {
							// ... the user is an editor for the current collection (at least for now -- we *might* want to make this more restrictive later)
							if (this.lp_context && this.lp_context.user_is_lp_admin() && !this.lesson_context) {
								o.publish_status = true
							}
						}
					}
				}
			}
			// PW: this is the previous (prior to 7/14/2026) version of this switch
			// o.publish_status = this.site_config.asset_manager_enabled && ((this.user_info.user_id == this.item_creator_id && (this.item_to_show.publish_status != 'published' || this.open_to_publish_status)) || this.user_is_admin)

			// console.log('user_info.user_id: ', this.user_info.user_id,  'item_creator_id: ', this.item_creator_id,  'item_to_show.publish_status: ', this.item_to_show.publish_status, 'open_to_publish_status: ', this.open_to_publish_status)

			// if there are any values in option_overrides, apply those overrides!
			for (let key in this.option_overrides) {
				o[key] = this.option_overrides[key]
			}

			// temp resources currently cannot be edited or removed, nor do we do HQIM for them
			if (this.is_temp_resource) {
				o.edit = false
				o.remove = false
				o.publish_status = false
			}

			// apply some changes when in lti_chooser
			if (this.lti_chooser) {
				o.report_issue = false
				o.copy_item_for_my_use = false
				o.publish_status = false
				o.edit = false
				o.copy = false
				o.remove = false
			}

			return o
		},

		item_title() {
			// allow includer to override what shows up for the title
			if (!empty(this.title_override)) {
				return this.title_override
			}

			let s
			if (this.is_lesson) s = this.item_to_show.lesson_title
			else s = this.item_to_show.description

			// add human_code to the start if we have one, in some cases...
			if (!empty(this.item_to_show.human_code)) {
				// TODO: make this configurable
				let human_code = this.item_to_show.human_code

				// lessons
				// SP.GK.U1.L1 -> Lesson 1.1
				human_code = human_code.replace(/^\w+\.\w+\.U(\w+)\.L(\d+)$/, "Lesson $1.$2")

				// activities -- don't do this for now, because they have the code embedded (kind of)
				// SP.GK.U1.L1.A1 -> Activity 1.1.1
				// human_code = human_code.replace(/^\w+\.\w+\.U(\w+)\.L(\d+)\.A(\w+)$/, "Activity $1.$2.$3")

				// assessments -- don't do this for now, because what you need is probably in the name

				if (human_code != this.item_to_show.human_code) {
					s = `<b>${human_code}</b> ${s}`
				}
			}

			if (this.item_to_show.restricted) s += '<i class="ml-2 fas fa-lock"></i>'

			// if the item lives in another unit, show a star to indicate this in the title? no this probably isn't needed
			// if (!empty(this.from_other_unit_string)) s += ' *'
			return s
		},

		item_type() {
			return this.is_lesson ? 'lesson' : this.item.type
		},

		item_id() {
			return this.is_lesson ? this.item_to_show.lesson_id : this.item_to_show.resource_id
		},

		item_creator_id() {
			return this.is_lesson ? this.item_to_show.creator_user_id : this.item_to_show.creator
		},

		item_created_by_this_user() {
			if (this.user_info.user_id == 0) return false
			return this.item_creator_id == this.user_info.user_id
		},

		user_created_item_tooltip() {
			if (this.item_copy_for_user) return `You have copied this ${this.item_noun(true)} for your use.`
			else return `You are the creator of this ${this.item_noun(true)}.`
		},

		course_unit_header() { 
			// in this version of Cureum, don't show the course name in the header in lessons
			return ''
			return this.item.course_unit_header() 
		},		// only used for lessons

		date_header() { return this.item.date_header() },				// only used for lessons

		card_color_lesson() {
			const { lesson, unit_plan } = this.site_config.asset_type_color
			// different color for unit guides/plans -- pretty hackish...
			if (/.*\bUnit\b.*\bGuide\b/.test(this.item.lesson_title ?? '')) {
				return unit_plan
			} else {
				return lesson || '#2962ff' // $v-blue-accent-4
			}
		},

		card_color_sparkl() {
			const { sparkl_activity } = this.site_config.asset_type_color
			return sparkl_activity || '#651fff' // $v-deep-purple-accent-3
		},

		card_color_sparkl_bank() {
			const { sparkl_activity_bank } = this.site_config.asset_type_color
			return sparkl_activity_bank || '#D50000' // $v-red-accent-4
		},

		card_color_assessment() {
			const { assessment } = this.site_config.asset_type_color
			return assessment || '#c51162' // $v-pink-accent-4
		},

		card_color_leader_resource() {
			const { leader_resource } = this.site_config.asset_type_color
			return leader_resource || '#4e342e' // $v-brown-darken-3
		},

		card_color_teacher_resource() {
			const { teacher_resource } = this.site_config.asset_type_color
			return teacher_resource || '#00695c' // $v-teal-darken-3
		},

		card_color_student_resource() {
			const { student_resource } = this.site_config.asset_type_color
			return student_resource || '#e65100' // $v-orange-darken-4
		},

		color_for_asset_type() {
			switch (true) {
				case this.item_type == 'lesson':
					return this.card_color_lesson
				// PW: use sparkl color for google assignments
				case this.item_type == 'sparkl' || this.item_type == 'google':
					return this.card_color_sparkl
				case this.item_type == 'sparkl_bank':
					return this.card_color_sparkl_bank
				case this.item_type == 'assessment':
					return this.card_color_assessment
				case this.item.lp_category == 'leader_resource':
					return this.card_color_leader_resource
				case this.item.teacher_facing:
					return this.card_color_teacher_resource
				default:
					return this.card_color_student_resource
			}
		},

		card_class() {
			let c = ''

			// SF 10/24: This was previously used to set the background color of the cards based on asset type
			// We have moved this determination to site config and the preceding `color_for_asset_type` computed property.
			// However, leaving these class additions here as it is referenced in CollectionResourceFolder
			// if (this.item_type == 'lesson') c += ' k-resource-collection-item--lesson'
			// else if (this.item_type == 'sparkl') c += ' k-resource-collection-item--sparkl-activity'
			// else if (this.item_type == 'sparkl_bank') c += ' k-resource-collection-item--sparkl-bank'
			// else if (this.item_type == 'assessment') c += ' k-resource-collection-item--assessment'
			// else if (this.item.lp_category == 'leader_resource') c += ' k-resource-collection-item--leader-resource'
			// else if (this.item.teacher_facing) c += ' k-resource-collection-item--teacher-resource'
			// // else if (this.item.target_students == 'ese') c += ' k-resource-collection-item--ese'
			// // else if (this.item.target_students == 'adv') c += ' k-resource-collection-item--adv'
			// else c += ' k-resource-collection-item--student-resource'

			if (this.show_all_standards && this.full_width_resource) c += ' k-resource-collection-item-show-all-standards'

			// if (this.search_match == 'yes') c += ' k-resource-collection-item--search_match'
			if (this.$store.state.last_viewed_resource_id == this.item_id) c += ' k-resource-collection-item--last-viewed'

			// add class to reduce font size for long names
			let title_text = U.html_to_text(this.item_title)
			let title_len = title_text.length
			let word_len = title_text.split(/\s+/).sort((a,b)=>b.length-a.length)[0].length
			if (title_len > 45 || word_len > 12) c += ' k-resource-collection-item-really-long-title'
			else if (title_len > 30 || word_len > 9) c += ' k-resource-collection-item-long-title'

			if (this.large) c += ' k-resource-collection-item-large'

			// add a class if the user has made a copy for their use
			if (this.item_copy_for_user) {
				// but don't apply this class if the user is editing the unit (where you always edit the original), or (in the default collection (where everything is yours!) and not in the lesson editor)
				if (!this.in_unit_editor && !(this.viewing_my_default_collection && !this.in_lesson_editor)) c += ' k-resource-collection-imported-to-my-collections'
			}

			return c
		},

		quick_look_content_class() {
			let s = 'k-resource-collection-item-quick-look-card-dialog'
			// add class if we're shifted over because of HQIM or because the user wants to see other things when viewing a lesson
			if (!this.quick_look_shifted && (this.item_shifted || this.lp_context?.shifted_for_item)) {
				s += ' k-resource-collection-quick-look-item-card-dialog-shifted'
			}
			// if we're in the reslink view, add class that shifts things so you can see the banner
			if (this.in_reslink_view) {
				s += ' k-resource-collection-item-quick-look-card-dialog-reslink'
			}
			return s
		},

		icon() {
			if (this.item_type == 'lesson') return 'fa-rectangle-list'
			// if not a lesson, use Resource/Activity.icon()
			return this.item_to_show.icon()
		},

		// this is a link that opens this asset (resource or lesson) in the context of this cureum instance
		standalone_cureum_link() {
			return this.item_to_show.standalone_cureum_link()
		},

		direct_resource_url() {
			return this.item_to_show.direct_resource_url()
		},

		inline_document_src() {
			if (!this.show_inline_document) return ''

			// for equella documents, or any pdf that isn't an upload, go through a proxy
			// TODO: put the domains to handle in this way in site_config
			if (this.direct_resource_url.includes('lor2.gadoe.org') || (this.item_to_show.is_pdf() && this.item_to_show.type != 'upload')) {
				let proxy_url = '/src/services/proxyfile.php?url=' + encodeURIComponent(this.direct_resource_url)
				return proxy_url
			}
			return this.direct_resource_url
		},

		resource_is_youtube() {
			if (!this.is_non_sparkl_resource) return false
			return this.item_to_show.is_youtube()
		},

		youtube_embed_url() {
			if (!this.is_non_sparkl_resource) return ''
			let video_id = U.youtube_id_from_url(this.item_to_show.url)
			// <iframe width="560" height="315" src="https://www.youtube.com/embed/ps80qZbbdbM?si=X3RFlF1i6kkk__Bv" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>
			return `https://www.youtube.com/embed/${video_id}`
		},

		resource_iframeable() { 
			if (!this.is_non_sparkl_resource) return false
			if (this.icon.includes('pdf')) return true
			return this.item_to_show.is_iframeable() || this.resource_is_youtube 
		},

		resource_is_image() { 
			if (!this.is_non_sparkl_resource) return false
			return this.item_to_show.is_image()
		},

		copyable_title() {
			return $(sr('<div>$1</div>', this.item_title)).text()
		},

		resource_date_display_class() {
			// need extra space for lesson start/end dates
			if (this.viewing_my_shadow_unit && this.is_lesson) {
				return 'ml-2'
			}
			return ''
		},

		resource_date_display() {
			// when viewing a lesson "my content", show the start/end dates for the lesson
			if (this.viewing_my_shadow_unit && this.is_lesson) {
				return this.item.date_header()
			}

			if (!this.item.created_at) return ''	// tcc items don't have dates

			let d
			if (this.is_lesson) {
				d = new Date(this.item.created_at*1000)
			} else {
				d = date.parse(this.item.created_at, 'YYYY-MM-DD HH:mm:ss')
			}
			return date.format(d, 'MMM D, YYYY')	// Jan 3, 2020 3:04 PM
			// return date.format(d, 'MMM D, YYYY h:mm A')	// Jan 3, 2020 3:04 PM
		},

		todo_complete: {
			get() {
				// todo_status value is a timestamp, so if it's > 100, it's complete
				// note that the todo_status for a video may be 5-95, indicating partial completion
				return this.user_info.todo_status[this.item.resource_id] > 100
			},
			set(val) {
				// record new value in database; the dispatch will update the store
				// if complete, we send a value of 100, which will get saved in the db as the timestamp
				let payload = {resource_id: this.item.resource_id, todo_status: (val ? 100 : 0)}
				U.loading_start()
				this.$store.dispatch('save_resource_completion', payload).then(x=>U.loading_stop()).catch(x=>U.loading_stop())
			}
		},

		is_required_video() {
			return this.item.type == 'video' && this.item.todo && U.youtube_id_from_url(this.item.url)
		},

		from_ca_mapping() {
			if (this.viewing_my_default_collection) {
				let default_collection_id = this.my_default_collection && this.my_default_collection.default_collection_id_for_collection_asset_mapping()
				return this.my_ca_mappings.find(x=>x.asset_id==this.item_id && x.collection_id!=default_collection_id)
			}
			return null
		},

		from_collection() {
			if (!this.from_ca_mapping) return null
			return this.all_courses.find(x=>x.lp_id==this.from_ca_mapping.collection_id)
		},

		from_collection_string() {
			if (!this.from_ca_mapping) return null
			if (!this.from_collection && !this.all_courses_loaded) {
				this.$store.dispatch('get_all_courses', 'initial no_loader')
			}
			
			if (this.from_collection?.title) return this.from_collection.title
			else return 'Course ID ' + this.from_ca_mapping.collection_id
		},

		from_other_unit_string() {
			// if the user is an lp admin and this item doesn't actually live in this unit, show them this so that they know where to find the item to edit
			if (!this.lp_context || !this.unit_context) return ''
			if (!this.lp_context?.user_is_lp_admin()) return ''
			if (this.lesson_context) return ''	// if this is true we're showing the item in a lesson
			
			if (this.is_lesson) {
				if (!this.unit_context.lessons.find(x=>x.lesson_id == this.item_id)) {
					for (let unit of this.lp_context.units) {
						if (unit.lessons.find(x=>x.lesson_id == this.item_id)) {
							// console.warn('here: ', object_copy(unit))
							let s = unit.title
							if (unit.display_number) s = unit.display_number + ': ' + s
							return `*Lives in “${s}”`
						}
					}
					return '*This lesson does not “live” in this unit.'
				}

			} else {
				if (!this.unit_context.resources.find(x=>x.resource_id == this.item_id)) {
					for (let unit of this.lp_context.units) {
						if (unit.resources.find(x=>x.resource_id == this.item_id)) {
							// console.warn('here: ', object_copy(unit))
							let s = unit.title
							if (unit.display_number) s = unit.display_number + ': ' + s
							return `*Lives in “${s}”`
						}
					}
					return '*This resource does not “live” in this unit.'
				}
			}
			return ''
		},

		resource_extensions() {
			if (this.is_lesson) return ''

			let html = ''

			/** If we have subject and grade from frameworks show here */
			if (this.item_to_show.subjects?.length){
				html += `<li><b class="grey--text text--darken-3">Subject${this.item_to_show.subjects.length > 1 ? 's' : ''}: </b>`
				html += this.item_to_show.subjects.map(subj => subj.text).join(', ')
				html += '</li>'
			}
			if (this.item_to_show.grades?.length){
				html += `<li><b class="grey--text text--darken-3">Grade${this.item_to_show.grades.length > 1 ? 's' : ''}: </b>`
				html += this.item_to_show.grades.join(', ')
				html += '</li>'
			}

			// some sites, e.g. ALEX, include "extensions" in their resources
			let ext = this.item_to_show.extensions
			if (ext) {
				if (ext.content_source) html += `<li><b class="grey--text text--darken-3">Content Source:</b> ${ext.content_source}</li>`
				if (ext.content_type) html += `<li><b class="grey--text text--darken-3">Content Type:</b> ${ext.content_type}</li>`

				// if we have license info, show it
				let s = ''
				if (!empty(ext.license_text)) s = ext.license_text
				else if (ext.license_uri) s = ext.license_uri
				if (s) {
					if (ext.license_uri) s = `<a href="${ext.license_uri}" target="_blank" style="text-decoration:none; color:rgba(0, 0, 0, 0.87); font-size:12px;">${s}</a>`
					html += `<li><b class="grey--text text--darken-3">License:</b> ${s}</li>`
				}

				// TODO: accessibility data

				if (ext.approved_date) html += `<li><b class="grey--text text--darken-3">Approved date:</b> ${ext.approved_date}`
			}

			if (this.is_non_sparkl_resource) html += `<li style="margin-top:5px; margin-bottom:5px; line-height:14px;"><b class="grey--text text--darken-3">URL:</b> <span style="font-size:12px">${this.item_to_show.direct_resource_url()}</span></li>`

			if (html) html = `<ul>${html}</ul>`
			return html
		},

		lesson_editor_title_style() {
			// try to make sure lesson title fits in the space we have alloted; if it doesn't, we get an ugly extra scroll bar
			let s = U.get_max_font_size_for_block_width({size_hi:18, size_lo:12, width:700, val:U.html_to_text(this.item_title)})
			return `font-size:${s}px`
		},

		quick_look_max_width() {
			if (this.publish_status_showing || this.resource_iframeable) return 1000
			else return 820
		},

		card_accessible_label() {
			return `Open ${this.item_noun(true)}: ${U.html_to_text(this.item_title)}`
		},

		card_has_popup() {
			return this.is_lesson || !this.is_non_sparkl_resource || this.resource_iframeable || this.quick_look_only || this.lti_chooser
		},

		card_expanded() {
			return this.lesson_showing || this.resource_quick_look_showing
		},

		// in the LTI chooser, the "merge_site_config" commit doesn't seem to run properly, 
		// which can make references to site_config.asset_manager.text break (e.g. CPS)
		asset_manager_text() {
			return this.site_config.asset_manager.text ?? {}
		},

		current_publish_status() {
			return this.$get_publish_status_data(this.item_to_show.publish_status)
		},

		hqim_menu_text() {
			// unpublished/rejected (and empty) statuses all resolve to the 'unsubmitted' key
			return this.current_publish_status.key != 'unsubmitted' ? `Show ${this.asset_manager_text.status}` : `Submit ${this.is_lesson ? 'Lesson' : (this.is_sparkl ? 'Activity' : 'Resource')} for Publication`
		},

		unpublished_display() {
			return this.show_unpublished && !this.item_to_show.agency_sanctioned && !this.item_copy_for_user
		},

		show_new_indicator() {
			if (!this.site_config.new_indicator.enabled) return false
			if (!this.item_to_show.new_until_date) return false
			let t = new Date()
			let today_str = `${t.getFullYear()}-${String(t.getMonth()+1).padStart(2,'0')}-${String(t.getDate()).padStart(2,'0')}`
			return this.item_to_show.new_until_date >= today_str
		},
	},

	watch: {
		// not currently using the tcc checkboxes on individual resources; only folders
		// checkbox_initially_checked: { immediate:true, handler() { this.checkbox_checked = this.checkbox_initially_checked }},
		using_llm() {
			// when this changes, the user has clicked to switch which lession variant they want to use
			// TODO: also if we start supporting switching, we need to apply the switching code to DirectivesWrapper/DirectivesListItem as well
			if (this.using_llm) {
				if (this.lesson_being_edited.lp_variant != 'B') {
					this.$alert({title:'Heads Up!', text:'Please note: if you convert this lesson to the Lesson Plan Companion format and save the converted lesson, you will <b>NOT</b> be able to convert the lesson back to its original format.'})
					// this will trigger conversion to variant B
					this.$store.commit('set', [this.lesson_being_edited, 'lp_variant', 'B'])
				}
			} else {
				if (this.lesson_being_edited.lp_variant != 'A') {
					this.$store.commit('set', [this.lesson_being_edited, 'lp_variant', 'A'])
				}
			}
		}
	},

	created() {
		if (this.in_reslink_view) {
			this.card_clicked()
		} else {
			if (this.quick_look_only) {
				this.resource_quick_look_showing = true
			}

			if (this.open_to_publish_status) {
				this.publish_status_showing = true
			}
		}
	},

	mounted() {
		// somewhat hackish way to determine if we are currently in the unit editor
		setTimeout(x => {
			if ($(vapp.collection_unit_editor?.$el).is(':visible')) {
				this.in_unit_editor = true
			}
			if ($(this.$el).parents('.k-lesson-edit').length > 0 || $(this.$el).parents('.k-lpe-lesson-edit').length > 0) {
				this.in_lesson_editor = true
			}
		}, 50)
		if (this.item.is_new_resource == true) {
			this.edit_item_start()
		}
	},

	methods: {
		card_keydown(evt) {
			if (evt.key === 'Enter' || evt.key === ' ') {
				evt.preventDefault()
				this.card_clicked()
			}
		},

		icon_color(hover) {
			// if we're in full-width mode, icon gets the color_for_asset_type
			if (this.full_width_resource) {		// || $(this.$el).parents('.k-resource-folder-resource-item-full-wrapper').length > 0) {
				return this.color_for_asset_type
			}
			// for a "tile", we use white, with a change in opacity while hovered
			if (hover) return 'rgba(255,255,255,0)'
			else return 'rgba(255,255,255,0.8)'
		},

		item_noun(capitalize) {
			return this.item.type_label(capitalize)
		},

		show_original() {
			if (this.is_lesson) {
				this.close_lesson()
				this.$nextTick(x=>{
					this.force_show_original = true
					this.card_clicked()
				})
			} else {	// sparkl
				this.force_show_original = true
				this.show_sparkl()
			}
		},

		show_my_copy() {
			if (this.is_lesson) {
				this.close_lesson()
				this.$nextTick(x=>{
					this.force_show_original = false
					this.card_clicked()
				})
			} else {	// sparkl
				this.force_show_original = false
				this.show_sparkl()
			}
		},

		card_clicked(flag) {
			// PW 1/1/2025: sometimes we show legacy google assignments; for now we will just say these aren't openable at this time
			if (this.item_type == 'google') {
				this.$alert('Google Assignments are not openable at this time.')
				return
			}

			let log_resource_id = this.item_to_show.resource_id
			let log_resource_title = this.item_title
			let log_action = 'view'

			let show_quick_look = false
			if (this.item.is_lesson_temp_resource === true) {
				// this is a "temp resource" item added in the lesson view tool; here we need to look up the full record for the resource before loading it.
				this.get_resource_record('re-click')
				return

			// if a lesson, show it
			} else if (this.item_type == 'lesson') {
				if (this.quick_look_only) {
					this.open_resource_in_new_window()
					return
				}

				// cancel previous item shift if necessary (the previous shifted item would be in a different ResourceCollectionItem)
				this.$emit('item_shift_update', {cancel_last: true})

				this.lesson_showing = true

				// log values are different for lessons
				log_resource_id = this.item_to_show.lesson_id
				log_resource_title = this.item_to_show.lesson_title
				log_action = 'view_lesson'

				this.$emit('lesson_opened')

			} else if (this.item_type == 'assessment') {
				// TODO: ???
				// for assessments, we set show_assessment_options to true to show links + instructions
				this.show_assessment_options = true
			
			// if is a required PD video, we open it in a special player
			} else if (this.is_required_video) {
				this.show_video_player = true

			// HACK: high demand career pathways tool
			} else if (this.item.url == 'hdcp') {
				this.$router.push({ path: '/hdcp' })

			/////////////
			// deal with conditions where we might show the quick_look interface...
			} else if (
				// there are some situations where we always pass through below to show the resource...
				// if flag is 'from_quick_look', the user was already on the quicklook, so pass through
				flag != 'from_quick_look' 
				&&
				// if we're in the reslink_view and this is a sparkl resource, pass through
				!(this.in_reslink_view && (this.is_sparkl))
				&& 
				// ...otherwise, show the quicklook view if...
				(
					// ...the resource is iframeable
					this.resource_iframeable
					||
					// ... or quick_look_for_all_resources is set to 'yes'
					this.site_config.quick_look_for_all_resources == 'yes' 
					|| 
					// ... or we're in the context of a lesson
					!empty(this.lesson_context) 
					|| 
					// ... or the item has a long_description, the item *isn't* a sparkl or sparkl_bank
					(!empty(this.item_to_show.long_description) && !this.is_sparkl && !this.is_sparkl_bank)
					||
					// ... or we're in the lti chooser
					this.lti_chooser
				)
			) {
				show_quick_look = true

			///////////////
			// If we get to any of the below clauses, we will show the resource
			// if type is 'collection_item', or type is 'lti', call get_resource_record to get the full resource data and retrieve the lti_form if necessary
			} else if (this.item_type == 'collection_item' || this.item_type == 'lti') {
				// (note that lti forms are timestamped, so we need to get a new form every time the link is launched)
				// PW: I don't think we meant to not log these...
				// log_resource_usage = false
				this.get_resource_record('launch')

			} else if (this.item_type == 'sparkl') {
				log_action = 'view_sparkl'
				// if the user is allowed to edit the activity, show in edit mode (for sparkl activities we don't distinguish between edit and view mode)
				let embed_mode = this.option_availability.edit ? 'edit' : 'view'
				this.show_sparkl(embed_mode)

			} else if (this.item_type == 'sparkl_bank') {
				log_action = 'view_sparkl_bank'
				// if the user is allowed to edit the bank, show in edit mode (for sparkl activities we don't distinguish between edit and view mode)
				let embed_mode = this.option_availability.edit ? 'edit' : 'view'
				this.show_sparkl(embed_mode)

			} else {
				// else open the direct_resource_url in a new window
				window.open(this.direct_resource_url, '_blank')
			}

			// mark as the last-viewed resource -- but we have to delay because CollectionResourceFolder might call clear_last_viewed_resource
			setTimeout(x=>this.mark_as_last_viewed_resource(), 0)

			// show quick_look if told to do so
			if (show_quick_look) {
				this.show_quick_look()

				// then unless we're showing the resource itself in the iframe, return here so that we *don't* log this as a resource view
				// (if the user clicks to actually open the item, we'll log when that happens)
				if (!this.resource_iframeable) {
					return
				}
			}

			// log signed or unsigned in users viewing resources
			let uemail = (this.signed_in) ? this.user_info.email : 'unsignedin@cglt.com'
			let uid    = (this.signed_in) ? this.user_info.user_id : 3859
			let school = (this.signed_in && this.user_info.district_department) ? this.user_info.district_department.join(',') : ''

			this.$store.dispatch('log_resource_usage', {
				'resource_id': log_resource_id,
				'resource_title': log_resource_title,
				'action': log_action,
				'user_id': uid,
				'user_email': uemail,
				'system_role': this.user_info.system_role,
				'school': school,
				// course_code is added by the dispatch fn
			})
			
			// for a todo item, this allows the user to click to claim that they've finished the item
			this.link_clicked = true
		},

		lti_chooser_preview() {
			// this action will get logged by card_clicked
			this.card_clicked('from_quick_look')
			
			// for sparkl, call this fn in LTIChooserCureum, so that if the user clicks "BACK" in LTIChooserSparkl, we'll close the preview
			if (this.is_sparkl) {
				vapp.lti_chooser_component.set_sparkl_preview_back_fn()
			}
		},

		lti_chooser_choose() {
			// log the action of choosing the activity
			let log_resource_id = this.item_to_show.resource_id
			let log_resource_title = this.item_title
			let uemail = (this.signed_in) ? this.user_info.email : 'unsignedin@cglt.com'
			let uid    = (this.signed_in) ? this.user_info.user_id : 3859
			let school = (this.signed_in && this.user_info.district_department) ? this.user_info.district_department.join(',') : ''

			this.$store.dispatch('log_resource_usage', {
				'resource_id': log_resource_id,
				'resource_title': log_resource_title,
				'action': 'lti_chooser_choose',
				'user_id': uid,
				'user_email': uemail,
				'system_role': this.user_info.system_role,
				'school': school,
				// course_code is added by the dispatch fn
			})

			// somewhat hackish: if this fn is being called, we must be in the context of the LTIChooserCureum component, which is accessible like this...
			vapp.lti_chooser_component.lti_chooser_choose(this.lti_activity_id, this.lti_activity_title)
		},

		mark_as_last_viewed_resource() {
			this.$store.commit('set', ['last_viewed_resource_id', this.item_id])
		},

		get_resource_record(flag) {
			this.$store.commit('set', [this.item_to_show, 'full_resource_data_loaded', true])

			let payload = {resource_id: this.item_to_show.resource_id}
			if (flag == 'launch') payload.get_lti_form = 'yes'

			this.$store.dispatch('get_resource_record', payload).then(result => {
				// fill in parts of the resource that we need but might not have been fully filled in previously, including overwriting the type
				this.$store.commit('set', [this.item_to_show, 'type', result.resource_record.type])
				this.$store.commit('set', [this.item_to_show, 'url', result.resource_record.url])
				this.$store.commit('set', [this.item_to_show, 'long_description', result.resource_record.long_description])

				// make sure is_lesson_temp_resource is *not* true (this param is not a formal part of the resource object)
				this.item_to_show.is_lesson_temp_resource = false

				// if flag is true, launch the link once we get the record data back
				if (flag == 'launch') {
					// if we got back an lti_form, launch to new window
					// NOTE: in a "direct" resource launch (/reslink path), managed through ResourceLinkView,
					// LTI launches originally happen in a full-screen iframe, so that a new window is not spawned
					if (!empty(result.lti_form)) {
						Resource.open_lti_resource_from_lti_form(result.lti_form, payload.resource_id)
					} else {
						// else we just open the new window to the resource's direct_resource_url
						window.open(this.direct_resource_url, '_blank')
					}

				} else if (flag == 're-click') {
					this.card_clicked()
				}
			})
		},

		async add_to_gc() {
			console.warn('add_to_gc in cureum')
			let use_addon

			// if site_config.use_google_classroom_addon is 'yes', we *always* use the addon
			if (this.site_config.use_google_classroom_addon == 'yes') {
				use_addon = true

			} else if (this.site_config.use_google_classroom_addon == 'optional') {
				let text = `<ul>`
					+ `<li>If your district or school has enabled the <b>${this.site_config.sparkl_app_name} Google Add-on</b>, click the “CREATE ADD-ON ATTACHMENT” button below.`
					+ `<div class="mt-1 ml-6" style="font-size:14px; line-height:17px">The Add-on allows for SSO and grade pass-back with Google Classroom.</div>`
					+ `</li>`
					+ `<li class="mt-3">Otherwise click “SHARE TO CLASSROOM” to create an Google Classroom assignment that links to this ${this.item_noun(true)}.`
					+ `<div class="mt-1 ml-6" style="font-size:14px; line-height:17px">Students may need to Sign In With Google to access the ${this.item_noun(true)}.</div>`
					+ `</li>`

				let val = await U.confirm_promise({
					title: 'Add to Google Classroom',
					text: text,
					acceptText: 'SHARE TO CLASSROOM',
					extraBtnText: 'CREATE ADD-ON ATTACHMENT',
					dialogMaxWidth: 720,
				})
				if (!val) return
				
				if (val === 'extra') use_addon = true
				else use_addon = false
				
			// if site_config.use_google_classroom_addon is 'no' (default, we *never* use the addon
			} else {
				use_addon = false
			}

			let resource_to_add = this.item_to_show

			////////////////////////
			// if use_addon is 'no' and this is a sparkl activity, the teacher must own the activity;
			// let them make a copy if necessary
			if (!use_addon && this.is_sparkl && !this.item_created_by_this_user) {
				let text = `You must make a copy of this ${this.item_noun(true)} for your use before sharing it to Google Classroom. Would you like to copy the activity now?`

				let val = await U.confirm_promise({
					title: 'Copy Activity?',
					text: text,
					acceptText: 'Make Activity Copy',
					dialogMaxWidth: 600,
				})
				if (!val) return

				// user confirmed, so make the copy
				resource_to_add = await this.copy_sparkl_to_shadow_unit_for_gc()
				if (!resource_to_add) return
			}

			////////////////////////
			let logging_resource_id = resource_to_add.resource_id
			let logging_action = 'add_to_gc'
			let logging_title = resource_to_add.description

			// we currently don't enable linking a lesson to GC, but retain this code id needed for the future
			if (this.item_type == 'lesson') {
				logging_resource_id = resource_to_add.lesson_id // other types to handle?
				logging_action = 'add_to_gc_lesson'
				logging_title = resource_to_add.lesson_title
			}

			let school = (this.user_info.district_department) ? this.user_info.district_department.join(',') : ''

			this.$store.dispatch('log_resource_usage', {
				'resource_id': logging_resource_id,
				'resource_title': logging_title,
				'action': logging_action,
				'user_id': this.user_info.user_id,
				'user_email': this.user_info.email,
				'system_role': this.user_info.system_role,
				'school': school,
				// course_code is added by the dispatch fn
			})

			// if we're using the addon, open the interface for letting the teacher choose their GC course (from App.vue)
			if (use_addon) {
				let add_to_gc_data = {
					activity_title: this.lti_activity_title,
					activity_id: this.lti_activity_id,
				}
				vapp.add_google_classroom_addon(add_to_gc_data)

			// else use the "old-style" google classroom "Share To Classroom interface"
			} else {
				// here we need to use the direct_resource_url -- e.g. sparkl-ed.com/xxx or the "raw" url for a link
				let url = `https://classroom.google.com/share?url=${this.direct_resource_url}`
				// add title to url
				url += sr('&title=$1', this.copyable_title)
				window.open(url)
			}

			this.mark_as_last_viewed_resource()
		},

		open_in_heyc() {
			vapp.open_heyc(this.standalone_cureum_link)
		},

		// this is for places where we want to copy the "direct link" to the asset
		copy_link_to_clipboard() {
			let link
			// if this is a sparkl activity and use_reslinks_for_sparkl_urls is 'yes',
			if (this.is_sparkl && this.site_config.use_reslinks_for_sparkl_urls == 'yes') {
				// use the cureum link
				link = this.standalone_cureum_link

			// else use the direct resource url, (which will be the standalone cureum link in some cases, but not others
			} else {
				link = this.direct_resource_url
			}
			
			U.copy_to_clipboard(link)
			this.$inform({text: '<b>' + this.item_noun(true) + ' link copied to clipboard:</b><br>' + link, color:'green darken-3'})

			this.mark_as_last_viewed_resource()
		},

		// this is for places where we explicitly want the standalone cureum link
		copy_reslink_to_clipboard() {
			U.copy_to_clipboard(this.standalone_cureum_link)
			this.$inform({text: '<b>Resource link copied to clipboard:</b><br>' + this.standalone_cureum_link, color:'green darken-3'})
		},

		share_item(flag) {
			let shared_item_id, noun
			if (this.is_lesson) {
				noun = 'Lesson'
				shared_item_id = 'L' + this.item_to_show.lesson_id
			// note that for sparkl activities we use the sparkl activity_id, which is in the url field; copy_assets_for_adding_to_collection deals with this
			} else if (this.is_sparkl || this.is_sparkl_bank) {
				noun = this.is_sparkl_bank ? 'Activity Bank' : 'Activity'
				shared_item_id = 'A' + this.item_to_show.url
			} else {
				noun = 'Resource'
				shared_item_id = 'R-' + this.item_to_show.resource_id
			}

			vapp.share_item(noun, shared_item_id, flag)
		},

		print_resource() {
			if (this.show_inline_document) {
				let frm = document.getElementById('inline_iframe_' + this.item_to_show.resource_id).contentWindow
				frm.focus()	// focus on contentWindow is needed on some ie versions
				frm.print()
			} else {
				window['inline_iframe_' + this.item_to_show.resource_id].print()
			}
		},

		open_resource_in_new_window() {
			if (!this.show_inline_document) {
				window.open(this.standalone_cureum_link)
			} else {
				let w = window.open()
				this.write_inline_html(w)
			}
		},

		// allow the user to shift the lesson to the right of the screen, so that other resources in the lesson (e.g. unit planning guides) are viewable on the left
		// note that the btn to call this fn only shows if we are showing the lesson in the context of a collection
		toggle_quick_look_shift() {
			this.quick_look_shifted = !this.quick_look_shifted
			this.$nextTick(() => { this.set_quick_look_z_index() })
		},

		set_quick_look_z_index() {
			const content = document.querySelector('.k-resource-collection-item-quick-look-card-dialog')
			const wrapper = content && content.closest('.v-dialog__content')
			if (!wrapper) return
			if (this.quick_look_shifted) {
				if (!this.quick_look_original_z_index) this.quick_look_original_z_index = wrapper.style.zIndex
				wrapper.style.zIndex = '2000'
			} else {
				wrapper.style.zIndex = this.quick_look_original_z_index || '202'
				this.quick_look_original_z_index = null
			}
		},

		shift_item_from_button(val) {
			this.shift_item(val)
			if (!this.item_shifted && this.resource_quick_look_showing) {
				this.$nextTick(() => {
					// boost whichever dialog should stay on top as everything un-shifts back to center: the editor if one is open, else the lesson/resource view
					const selector = this.resource_being_edited ? '.k-resource-editor-outer' : '.k-resource-collection-item-lesson-card-dialog'
					const topEl = document.querySelector(selector)
					const topWrapper = topEl && topEl.closest('.v-dialog__content')
					if (topWrapper) topWrapper.style.zIndex = '300'
				})
			}
		},

		shift_item(val) {
			if (typeof(val) == 'boolean') this.item_shifted = val
			else this.item_shifted = !this.item_shifted

			if (!this.item_shifted) {
				const was_shifted = this.quick_look_shifted
				this.quick_look_shifted = false
				if (was_shifted) this.$nextTick(() => { this.set_quick_look_z_index() })
			}

			// emit an event that will bubble up to the collection, that causes the collection's resources to be shifted to the left; and makes it so that if the user switches to a different unit or collection, the shifted item will be closed and the collection will be unshifted.
			if (this.item_shifted) this.$emit('item_shift_update', {shifted_for_item:this})
			else this.$emit('item_shift_update', {shifted_for_item:false})
		},

		print_lesson() {
			this.show_print_dialog = true

			this.mark_as_last_viewed_resource()
		},

		close_lesson() {
			// make sure the lesson isn't shifted -- and that the collection also isn't shifted
			this.shift_item(false)
			this.lesson_showing = false
			this.force_show_original = false
			this.$emit('lesson_closed')
		},

		close_resource_view() {
			// make sure the resource view isn't shifted -- and that the collection also isn't shifted
			this.shift_item(false)
			this.resource_showing = false
		},

		// generic hook called by Collection.vue when a different item is shifted, so whichever view/dialog this item had shifted gets fully closed rather than just un-shifted
		close_shifted_item() {
			if (this.lesson_showing) this.close_lesson()
			else if (this.resource_showing) this.close_resource_view()
			else this.shift_item(false)
		},

		show_hqim_status() {
			this.publish_status_showing = true
			this.show_quick_look()
			if (this.lesson_showing) this.shift_item(true)
		},

		open_item_shifted() {
			this.shift_item(true)
			if (this.is_lesson) {
				if (!this.lesson_showing) this.lesson_showing = true
			} else if (this.is_non_sparkl_resource) {
				if (!this.resource_showing) this.resource_showing = true
			}
			// Sparkl activities: not yet supported here
		},

		show_quick_look() {
			// for html, we have to load it first...
			if (this.item_type == 'html') {
				this.show_inline_html = this.resource_quick_look_showing = true
				// get html fresh every time, in case of an edit
				$.ajax({
					url: this.direct_resource_url,
					success: (html)=>{
						if (html.search(/^([\s\S]*?)<hr>([\s\S]*)/) > -1) {
							this.inline_html_always = RegExp.$1
							this.inline_html_extra = RegExp.$2
						} else {
							this.inline_html_always = html
							this.inline_html_extra = ''
						}

						// call fn to write the inline html to the iframe that will be showing on nextTick
						this.$nextTick(x => this.write_inline_html())
					},
					cache: false,
				})
				return
			} 
			
			if (this.resource_iframeable) {
				this.show_inline_document = true
			}

			this.resource_quick_look_showing = true

			vapp.resource_quick_look_showing = this

			// note that if a lesson is shifted to the right and something in a quick look is then shown, the quick look will be shifted to the left via the content-class attribute on the quick look dialog
		},

		hide_quick_look() {
			this.show_inline_document = this.show_inline_html = this.resource_quick_look_showing = this.publish_status_showing = false

			// see AssetManager
			if (this.quick_look_only) {
				this.$emit('quick_look_closed')
			}
		},

		write_inline_html(w) {
			// we write inline html to an iframe so that it's printable and openable in a new window

			let writing_in_iframe = (!w || !w.document)

			if (writing_in_iframe) {
				w = window['inline_iframe_' + this.item_to_show.resource_id]
			}
			w.document.open()
			w.document.write('<html><head>')
			w.document.write(sr('<title>$1</title>', this.item_to_show.description))
			w.document.write('<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/froala-editor/4.0.10/css/froala_style.min.css" crossorigin="anonymous">')
			w.document.write('<link href="https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,400;0,900;1,400;1,900&display=swap" rel="stylesheet">')
			// MathLive styles; see also U.inject_mathlive_styles
			w.document.write(`<style>${U.mathlive_core_css}</style>`)


			w.document.write(`
				<style>
				body {
					font-family:Roboto, sans-serif;
					font-size:16px;
					line-height:1.4em;
					color:#000;
				}
				.no-print { display:none; }
				@media print {
					.no-print { display:block; }
				}
				</style>
				<script>
				window.toggle_more = function() {
					var el = document.getElementById('html_extra');
					if (el.getAttribute('data-showing') == 'yes') {
						el.setAttribute('data-showing', 'no');
						el.setAttribute('style', 'display:none');
					} else {
						el.setAttribute('data-showing', 'yes');
						el.setAttribute('style', 'display:block');
					}
				}
			`)
			w.document.write('</' + 'script>')

			w.document.write('</head><body>')

			w.document.write('<div class="fr-view" style="max-width:760px; margin-left:auto; margin-right:auto;">')
			w.document.write(sr('<h3 class="$1">$2</h3>', (writing_in_iframe?'no-print':''), this.item_to_show.description))
			w.document.write(sr('<div>$1</div>', U.render_latex(this.inline_html_always)))
			if (this.inline_html_extra) {
				w.document.write('<div style="border-top:1px solid #ccc;">')
				w.document.write('<div style="text-align:center; margin-top:12px; margin-bottom:8px"><button onClick="window.toggle_more()">Toggle More</button></div>')
				w.document.write(sr('<div id="html_extra" style="display:none">$1</div>', U.render_latex(this.inline_html_extra)))
				w.document.write('</div>')
			}
			w.document.write('</div>')

			w.document.write('</body></html>')
			w.document.close()
		},

		edit_item_restriction() {
			if (!this.option_availability.show_restricted_resources_control) return
			// let msg = `Items restricted to authorized teachers and staff will only be shown to users who have signed in to ${this.site_config.app_name} and who have a designated role of Teacher or Staff.<br><br>`
			let msg = `Items restricted to authorized teachers and staff will only be shown to users who have signed in to ${this.site_config.app_name}.<br><br>`

			let acceptText, cancelText
			if (this.item_to_show.restricted) {
				msg += `Access to this item is currently <b class="red--text text--darken-3">restricted</b>. Click the right button below to continue restricting the item, or click the left button to open the item to any users.`
				acceptText = 'Continue Restricting Item'
				cancelText = 'Do Not Restrict Item'

			} else {
				msg += `Access to this item is currently <b class="green--text text--darken-3">not restricted</b>. Click the left button below to leave the item unrestricted, or click the right button to restrict the item to authorized teachers and staff.`
				acceptText = 'Restrict Item'
				cancelText = 'Leave Item Unrestricted'
			}

			this.$confirm({
				// title: 'Are you a witch?',
				text: msg,
				acceptText: acceptText,
				acceptColor: 'red darken-3',
				acceptIcon: 'fas fa-lock',
				cancelText: cancelText,
				cancelColor: 'green darken-3',
				cancelIcon: 'fas fa-lock-open',
				dialogMaxWidth: 600,
			}).then(y => {
				// accept (right btn) == set restricted to true
				this.edit_item_restriction_save(true)
			}).catch(n=>{
				// cancel (left btn) == clear restricted flag
				this.edit_item_restriction_save(false)
			})
		},

		edit_item_restriction_save(val) {
			// this will definitionally only be called when you are allowed to edit the resource
			this.$store.commit('set', [this.item_to_show, 'restricted', val])

			if (this.item_type == 'lesson') {
				// for save_lesson we only need to send in the lesson data we want to change, here just the restricted value
				U.ajax('save_lesson', {
					user_id: this.user_info.user_id,
					lesson_class: 'teacher',	// this won't actually matter
					lesson_data: JSON.stringify({lesson_id: this.item_to_show.lesson_id, restricted: val})
				}, result => {
					if (result.status != 'ok') {
						console.error('Error in ajax call: ' + result.status); vapp.ping(); return;
					}

					// updated data will be returned; emit it to the parent component
					this.$emit('edit_item_saved', {type:'lesson', updated_lesson: result.updated_lesson, edited_lesson:this.item_to_show})	// pass edited_lesson so we can preserve *_showing values
				})

			} else {
				this.$store.dispatch('save_resource', { resource: new Resource(this.item_to_show) }).then(saved_resource_data => {
					U.loading_stop()

					let r = new Resource(saved_resource_data)
					// emit edit_item_saved to update the activity in whatever collection we're editing
					this.$emit('edit_item_saved', {type:'resource', updated_resource: r})
				}).catch(error=>{
					console.error(error)
					this.$alert(sr('A problem occurred when attempting to save the resource. Message from server:<div class="mt-2">$1</div>', error))
				})
			}
		},

		async edit_item_start(flag) {
			// we may need to check something about another course here, so get_all_courses if needed
			if (!this.all_courses_loaded) {
				this.$store.dispatch('get_all_courses', 'initial').then(x=>{
					this.edit_item_start(flag)
				})
				return
			}

			// if the user is requesting to edit an agency-sanctioned item (not a user copy, if there is a user copy) and isn't in the unit editor interface, make sure they really want to edit
			if (this.item_to_show.agency_sanctioned && !this.in_unit_editor) {
				if (flag != 'warning_accepted' && !this.$store.state.ignore_published_edit_warning) {
					// tell them that they can edit if they wish, but warn them about the consequences
					let title = 'Are you sure you want to edit?'
					let msg = `This ${this.item_noun(true)} is published for use by other teachers. Are you sure you want to edit it? You can alternatively make a copy of the ${this.item_noun(true)} for your own use, and then edit the copy.`
					this.$confirm({
						title: title,
						text: msg,
						acceptText: `Edit the ${this.item_noun(true)}`,
						secondaryCheckbox: 'Don’t show this warning again',
						secondaryCheckboxInitiallyOn: false,
						dialogMaxWidth: 480,
					}).then(arr => {
						// if user clicks the 'don't show this again' box, don't show it again (for the rest of this browser session)
						if (arr[1] == true) {
							this.$store.commit('set', ['ignore_published_edit_warning', true])
						}
						this.edit_item_start('warning_accepted')
						// ignore_published_edit_warning
					}).catch(n => {console.error(n)})
					return
				}
			}
			
			// this is called for a lesson, resource, or sparkl activity; open the appropriate editor
			// note that sparkl activities are edited from the sparkl window
			if (this.is_lesson) {
				if (this.lesson_being_edited) {
					this.$alert('You must close the editor for the lesson you’re currently editing before you start editing a different lesson.')
					return
				}
				// set lesson_being_edited_lesson_class; by default it's 'teacher'
				this.lesson_being_edited_lesson_class = 'teacher'
				// if this is a new lesson, look for a is_lesson_template flag
				if (this.item_to_show.lesson_id == 0) {
					if (this.item_to_show.is_lesson_template) this.lesson_being_edited_lesson_class = 'template'
				// else if we're editing an existing template lesson, class is 'template'
				} else if (this.item_to_show.lesson_id == this.item_to_show.lesson_template_id) {
					this.lesson_being_edited_lesson_class = 'template'
				}

				// get the lesson, even if we already loaded it, to make sure it hasn't been edited by someone else since it was last loaded
				const payload = {
					user_id: this.user_info.user_id,
					lesson_id: this.item_to_show.lesson_id
				}
				await this.$store.dispatch('load_lesson', this.item_to_show)
				// call check_home_collection_for_edit now, once we know the full lesson has been loaded; if check fails, return
				if (!(await this.check_home_collection_for_edit())) return

				// then check the lesson out for editing; if the service returns an error, the store will alert the user and reject
				U.loading_start()
				try {
					await this.$store.dispatch('check_out_lesson_for_editing', payload)
					U.loading_stop()
					vapp.rci_being_edited = this	// debugger
					this.lesson_being_edited = this.item_to_show
					this.lesson_editor_showing = true

					// set original_edited_lesson_lp_variant
					this.original_edited_lesson_lp_variant = this.lesson_being_edited.lp_variant

					// set using_llm based on the item; if switching is enabled, the user will see a checkbox they can use to toggle this
					this.using_llm = this.use_enhanced_lesson_editor

				} catch(err) {
					U.loading_stop()
					console.error(err)
				}
			
			} else if (this.is_sparkl || this.is_sparkl_bank) {
				// call check_home_collection_for_edit; if check fails, return
				if (!(await this.check_home_collection_for_edit())) return

				// note that currently (6/21/2023), we are only explicitly asked to edit when a new activity is added from DirectivesWrapper
				// PW: ???
				this.show_sparkl('edit')
				
			} else {
				// call check_home_collection_for_edit; if check fails, return
				if (!(await this.check_home_collection_for_edit())) return

				this.resource_being_edited = this.item_to_show
			}
			this.mark_as_last_viewed_resource()

			// bubble event up in case the caller wants to do something
			this.$emit('edit_item_start')
		},

		async check_home_collection_for_edit(flag) {
			// admins can always edit, regardless of home_collection_id
			if (this.user_is_admin) return true

			// if you're the creator of the item, you can definitely edit; but if not and the item has a home_collection_id, ...
			let home_collection_id = this.item_to_show.home_collection_id ?? 0
			if (home_collection_id != 0 && !this.item_created_by_this_user) {
				// we need to make sure you have editing rights to the home_collection_id
				let home_collection = this.all_courses.find(x=>x.lp_id==home_collection_id)

				// if home_collection isn't currently in this.all_courses, it might be from a previous school year...
				if (empty(home_collection)) {
					// try to load the summary of the collection now (we're just going to check permissions)
					try {
						home_collection = await this.$store.dispatch('get_learning_progression', {
							lp_id: home_collection_id,
							retrieve_summary_only: true,
						})
						// if we get back a string, get_lp returned an error...
						if (typeof(home_collection) == 'string') {
							// ... which might well be 'no_admin_rights', meaning the user can't edit it;  we'll return false below
							console.warn('non-ok status from get_learning_progression: ' + home_collection)
							home_collection = null
						}
					} catch(e) {
						console.error('unknown error (2) from get_learning_progression: ' + home_collection)
						home_collection = null
					}
				}

				// if we can't find the home collection, or the user isn't an editor for the home collection, don't allow editing
				if (empty(home_collection) || !home_collection.user_is_lp_admin()) {
					if (flag == 'alert') {
						this.$nextTick(x=>{
							if (empty(home_collection)) {
								this.$alert(`The “home collection” for this resource (collection_id ${home_collection_id}) could not be identified, so we cannot verify that you have sufficient rights to edit the resource. Please ask a system admin to edit the ${this.item_noun(true)}.`)
							} else {
								this.$alert(`This resource was created in a different collection (“${home_collection.title}”, Course Code ${home_collection.course_code}) that you do not have sufficient rights to edit. Please ask a system admin to edit the ${this.item_noun(true)}.`)
							}
						})
					}
					return false
				}
			}
			// if we get through the above, the user can edit
			return true
		},

		edit_resource_cancel() {
			// bubble up event in case a higher component needs to do something (e.g. remove a newly-added resource)
			this.$emit('edit_item_cancel', {type: 'resource', edited_resource: this.item_to_show})
			this.resource_being_edited = null
			if (!this.resource_showing) this.shift_item(false)
		},

		edit_resource_saved(updated_resource) {
			// the resource editor will have saved the resource to the db; bubble updated_resource up to the component that needs to handle it
			this.$emit('edit_item_saved', {type: 'resource', updated_resource: updated_resource})

			// update 
			if (this.item_type == 'html') {
				console.warn('refresh')
				this.show_quick_look()
			}
			// note that we don't call edit_resource_cancel because we don't want *that* event to bubble up
			this.resource_being_edited = null
			if (!this.resource_showing) this.shift_item(false)
		},

		edit_lesson_cancel(flag) {
			// if the user clicked to convert from variant A to B but didn't save, go back to variant A
			if (this.lesson_being_edited.lp_variant == 'B' && this.original_edited_lesson_lp_variant == 'A') {
				this.$store.commit('set', [this.lesson_being_edited, 'lp_variant', 'A'])
			}

			// bubble up event in case a higher component needs to do something (e.g. remove a newly-added resource)
			this.$emit('edit_item_cancel', {type: 'lesson', edited_lesson: this.item_to_show})
			// console.warn('reset 1: ' + flag)
			if (flag != 'already_checked_in') this.lesson_being_edited.reset_checked_out_for_edit()
			this.lesson_being_edited = null
			this.lesson_editor_showing = false
			if (!this.lesson_showing) this.shift_item(false)
		},

		edit_lesson_saved(args) {
			// TODO: update lesson...
			// if we were editing an existing lesson, update it here
			// we have to take care of this here because you might be, for example, viewing your copy of the lesson from the course resources area, in which case CourseResources.vue won't know to update your copy
			// if (this.item_to_show.lesson_id != 0 && this.item_to_show.lesson_id == args.updated_lesson.lesson_id) {
			// 	let index = this.my_lessons.findIndex(x=>x.lesson_id == args.updated_lesson.lesson_id)
			// 	if (index > -1) this.my_lessons.splice(index, 1, new Lesson(args.updated_lesson))
			// }

			// the lesson editor will have saved the resource to the db; bubble updated_resource up for further processing
			this.$emit('edit_item_saved', {type: 'lesson', updated_lesson: args.updated_lesson, edited_lesson: args.edited_lesson, flag: args.flag})

			// if the flag is 'and_close', close the editor
			if (args.flag == 'and_close') {
				this.lesson_being_edited = null
				this.lesson_editor_showing = false
				// and check the lesson back in so someone else can edit it if necessary
				// console.warn('reset 2')
				args.edited_lesson.reset_checked_out_for_edit()
				if (!this.lesson_showing) this.shift_item(false)
			} else {
				// after a tick, update lesson_being_edited, becase the save will have changed the item_to_show object. this change will be sent through to the lesson editor
				this.$nextTick(x=>{
					this.lesson_being_edited = this.item_to_show
					this.lesson_editor_showing = true
				})
			}

			// also, if this was a new lesson we also have to close the editor, so that the editor can be re-opened with the "full version" of the lesson
			// NO LONGER NEEDED??
			// if (this.item_to_show.lesson_id == 0) this.lesson_being_edited = null
			// note that we don't call edit_resource_cancel because we don't want *that* event to bubble up
		},

		duplicate_item(flag) {
			// we have to check the lp out for editing before being able to duplicate, unless we're in my_content or the sandbox or the unit editor
			if (!(this.viewing_my_default_collection || this.viewing_my_shadow_unit || this.in_unit_editor)) {
				// PW: I don't think this should happen...
				if (!this.lp_context) {
					this.$alert('You cannot duplicate this item from here (38492).')
					return
				}
				if (flag != 'checked_out') {
					this.$store.dispatch('edit_access_control', {lp_id: this.lp_context.lp_id, lp_updated_at: this.lp_context.updated_at, action: 'checkout'}).then((result)=>{
						this.duplicate_item('checked_out')
					}).catch((e)=>{
						// if this doesn't work, don't enter edit mode
						console.error('error requesting editor checkout for duplicate_item', e)
					})
					return
				}
			}

			// note that a version of duplicate_item also exists in DirectivesWrapper
			let noun, title, item_id
			if (this.is_lesson) {
				noun = 'Lesson'
				title = this.item.lesson_title
				item_id = this.item.lesson_id
			} else if (this.is_sparkl) {
				noun = 'Activity'
				title = this.item.description
				item_id = this.item.url
			} else {	// non-sparkl resource
				noun = 'Resource'
				title = this.item.description
				item_id = this.item.resource_id
			}
			this.$prompt({
				title: 'Duplicate ' + noun,
				text: `Enter a title for the duplicated ${noun}:`,
				initialValue: title,
				disableForEmptyValue: true,
				acceptText: 'Duplicate',
				acceptIcon: 'fas fa-copy',
			}).then(new_title => {
				new_title = $.trim(new_title)

				// follow the pattern from DirectivesWrapper / ResourceSearch - CollectionResourceFolder - CollectionUnit...
				// start by using copy_assets_for_adding_to_collection to copy the item
				let payload = {
					user_id: this.user_info.user_id, 
					resource_ids: [], 
					lesson_ids: [],
					agency_sanctioned: 'no',
					new_title: new_title,
				}

				// if we're using a sparkl_origin_override for this collection, send it in
				if (this.lp_context && !empty(this.lp_context.sparkl_origin_override)) {
					payload.sparkl_origin_override = this.lp_context.sparkl_origin_override
				}

				if (noun == 'Lesson') payload.lesson_ids.push(item_id)
				else payload.resource_ids.push(item_id)

				U.loading_start()
				U.ajax('copy_assets_for_adding_to_collection', payload, result => {
					U.loading_stop()

					if (result.status != 'ok') {
						// this shouldn't happen here
						if (result.status == 'bad_item_id') result.status = `An error occurred: invalid item ID (${item_id}).`
						this.$alert(result.status)
						return
					}

					// pass the copied item up through CollectionResourceFolder via add_duplicated_items (CollectionResourceFolder will add to the unit and save the unit)
					let args = {original_item: this.item, duplicated_items: []}
					if (noun == 'Lesson') {
						args.duplicated_items.push(new Lesson(result.copies[0].lesson_data))

					} else {
						args.duplicated_items.push(new Resource(result.copies[0].resource_data))
					}

					// add the similar to how we do it with search -- the handler of add_duplicated_items will add the item to whatever folder we're in and whatever collection we're saving
					this.$emit('add_duplicated_items', args)
				})

			}).catch(n=>{
				// if we checked out, need to check back in
				if (flag == 'checked_out') this.$store.dispatch('edit_access_control_checkin', {lp_id: this.lp_context.lp_id})
			})
		},

		pin_item(val) {
			/** TODO? */
		},

		// PW NOTE (3/5/2026): this is only applicable (I believe) for the original version of ALEX where we used an "Unpublished Items" collection
		// Once we know that ALEX no longer needs this, we can delete this fn (and the menu items that call it)
		unpublish_item() {
			let resources_button_text = this.site_config.unit_mode_toggle_option?.resources || 'Resources'
			let standards_button_text = this.site_config.unit_mode_toggle_option?.standards || 'Standards'
			this.$confirm({
				title: 'Unpublishing',
				text: `If you “unpublish” this resource:<ul><li>The item will be removed from the “${resources_button_text}” area of all course collection units and repository units</li><li>The item will not appear in the “${standards_button_text}” area of any course collection units or repository units</li><li>The item will not appear in site-wide searches</li><li>The item will be added to the “${this.site_config.unpublished_items_collection.title}” collection, in case you later want to re-publish the item</li></ul>`,
				acceptText: 'Unpublish Item',
				acceptColor: 'red darken-2',
				acceptIcon: 'fas fa-ban',
				dialogMaxWidth: 600,
			}).then(y => {
				let payload = {
					user_id: this.user_info.user_id, 
					unpublished_item_id: this.item_id,
					unpublished_item_type: (this.is_lesson) ? 'lesson' : 'resource',
				}
				U.loading_start()
				U.ajax('unpublish_item', payload, result => {
					U.loading_stop()
					if (result.status != 'ok') { this.$alert(result.status); return; }	// shouldn't happen

					this.$inform(`Item unpublished.`)

					// go through all collections we have in state
					for (let c of this.$store.state.all_courses) {
						// if c is the unpublished_items_collection, add the item to the first unit in this collection
						if (c.lp_id == this.site_config.unpublished_items_collection.lp_id) {
							// only need to add if the collection is fully loaded
							// NOTE: even if the collection's units haven't been fully loaded, it's OK to push the lesson/resource to the unit's lessons/resources array
							if (c.fully_loaded) {
								if (this.is_lesson) {
									this.$store.commit('set', [c.units[0].lessons, 'PUSH', new Lesson(this.item)])
									c.units[0].add_item_to_folder({
										type: 'lesson',
										resource_id: this.item.lesson_id,
										parent_folder_id: 'top',
									})
								} else {
									this.$store.commit('set', [c.units[0].resources, 'PUSH', new Resource(this.item)])
									c.units[0].add_item_to_folder({
										type: 'resource',
										resource_id: this.item.resource_id,
										parent_folder_id: 'top',
									})
								}
							}

						// otherwise if the collection_type is 'course' or 'repo'
						} else if (c.collection_type == 'course' || c.collection_type == 'repo') {
							// for each unit in the collection
							for (const unit of c.units) {
								// remove from the unit's resources/lessons/resource_tree structures; LP_Unit has a remove_item fn to do this
								unit.remove_item({item_id: this.item_id})
								// and if the item is in standards_aligned_assets, remove from there
								if (unit.standards_aligned_assets_loaded) {
									const arr = unit.standards_aligned_assets.filter(x=>x.asset_id != this.item_id)
									this.$store.commit('set', [unit, 'standards_aligned_assets', arr])
								}
							}
						}
					}
				})
			}).catch(n => {console.error(n)})

			// bubble event up in case the caller wants to do something
			this.$emit('unpublish_item_start')
		},

		async show_sparkl(embed_mode, force_reload) {
			// embed_mode can be 'edit' or 'view'. if we receive a value, use it
			if (typeof(embed_mode) != 'string') {
				// else if the user has the ability to edit here, use 'edit'
				if (this.option_availability.edit) embed_mode = 'edit'
				// else use 'view'
				else embed_mode = 'view'
				// (note that the embed mode doesn't do much more than affect what options are shown in the TeacherStart component)
			}

			// override sparkl_origin if the collection tells us to do so (used for AP Sparkl activities, e.g.)
			let sparkl_origin_override = null
			if (this.lp_context && !empty(this.lp_context.sparkl_origin_override)) {
				sparkl_origin_override = this.lp_context.sparkl_origin_override
			}

			// if we're not in the lti_chooser AND option_availablity says we *should* be able to edit, make sure we have all courses loaded, so that we can check_home_collection_for_edit below to determine if the user is allowed to edit 
			if (!this.lti_chooser && this.option_availability.edit) {
				if (!this.all_courses_loaded) {
					this.$store.dispatch('get_all_courses', 'initial').then(x=>{
						this.show_sparkl(embed_mode, force_reload)
					})
					return
				}
			}

			// force_allow_original_to_be_edited: by default this is 'no', meaning Sparkl can decide to prevent you from editing
			// but if we're not in the lti_chooser AND option_availablity says we *should* be able to edit...
			this.force_allow_original_to_be_edited = 'no'
			if (!this.lti_chooser && this.option_availability.edit) {
				// and the user is an editor for the "home" collection of the sparkl...
				if (await this.check_home_collection_for_edit('alert')) {
					// force Sparkl to let you edit (this allows, e.g., editors of agency_sanctioned collections to be able to edit activities in their collections)
					this.force_allow_original_to_be_edited = 'yes'
				}
				// Note: if the user isn't an editor for the "home" collection of the sparkl, we won't force sparkl to allow the user to edit, 
				// but we won't force sparkl to disallow the user to edit either, 
				// so in the unlikely event that the user is actually an editor in the sparkl DB, they *will* be able to edit
			}

			// force_prevent_original_to_be_edited: by default this is 'no', meaning Sparkl can decide to allow you to edit 
			// but if we're in the chooser, we never want editing, so prevent Sparkl from allowing you to edit
			if (this.lti_chooser) this.force_prevent_original_to_be_edited = 'yes'

			vapp.$refs.sparkl_embed.show_activity({
				resource_type: this.item_type,
				activity_record: {
					tool_activity_id: this.item_to_show.url,
					lti_resource_link_id: this.item_to_show.resource_id,
					activity_title: this.item_to_show.description,
					creator_user_id: this.item_to_show.creator,
				},
				force_reload: force_reload,
				embed_mode: embed_mode,

				force_allow_original_to_be_edited: this.force_allow_original_to_be_edited,
				force_prevent_original_to_be_edited: this.force_prevent_original_to_be_edited,
				in_lti_chooser: (this.lti_chooser ? 'yes' : 'no'),

				// if this isn't a bank resource and the user doesn't own the activity and hasn't already copied it for their use (and we're not in the lti_chooser), show the button offering them to let them copy it
				show_copy_for_my_use_btn: (this.item_type != 'sparkl_bank' && this.option_availability.copy_item_for_my_use && !this.lti_chooser),
				viewing_original_of_in_my_collections: this.force_show_original,
				viewing_copy_of_original: (this.item_copy_for_user != null && !this.force_show_original),
				controller_component: this,
				sparkl_origin_override: sparkl_origin_override,
			})

			// hide active dialogs/overlays while the activity shows (e.g. the unit editor might be showing)
			$('.v-dialog__content--active, .v-overlay--active').hide()
		},

		// this is called from SparklEmbed when the sparkl activity's connection is established with Cureum
		sparkl_activity_initialized(data) {
			// sparkl will send us the activity's editors; from here, we then tell sparkl about editing policies...
			let payload = {}

			// if we're going to let you edit -- either because sparkl thinks the user is an activity editor or we set force_allow_original_to_be_edited to 'yes' --
			// and cureum says the activity is agency_sanctioned, 
			// have Sparkl give a warning to the user if they actually try to edit
			if ((this.force_allow_original_to_be_edited == 'yes') || (vapp.$refs.sparkl_embed.activity_owned_by_user && this.force_prevent_original_to_be_edited != 'yes')) {
				if (this.item_to_show.agency_sanctioned) {
					payload.warning_text_before_edited = 'This activity is published for use by other teachers. Are you sure you want to edit it?'
				}
			}

			vapp.$refs.sparkl_embed.execute('establish_editing_policies', payload)
		},

		sparkl_activity_saved(activity_data_from_sparkl) {
			// this is called when Sparkl issues a 'sparkl_activity_saved' message
			// activity_data_from_sparkl should include sparkl_activity_id, stars_available, activity_instructions, activity_title, activity_editors, open_to_exercise_bank

			// if the user isn't allowed to edit the activity, return. Note that we're trusting the logic that sets option_availability.edit here
			if (!this.option_availability.edit) {
				return
			}

			// NOTE: Code below needs to be kept in synch with ResourceSearch (it's not identical, but some things are the same)
			let changed = false
			let resource_copy = this.item_to_show.copy_for_save()

			// if sparkl_activity_id is changed, set it
			if (activity_data_from_sparkl.sparkl_activity_id && activity_data_from_sparkl.sparkl_activity_id != resource_copy.url) {
				// PW 12/2023: I don't think this should ever happen anymore here, because we deal with new activities in ResourceSearch now
				resource_copy.url = activity_data_from_sparkl.sparkl_activity_id
				changed = true
			}
			
			// ditto the activity title
			if (activity_data_from_sparkl.activity_title && activity_data_from_sparkl.activity_title != resource_copy.description) {
				resource_copy.description = activity_data_from_sparkl.activity_title
				changed = true
			}

			// ditto case_alignments
			if (activity_data_from_sparkl.case_alignments && !this.item_to_show.standards_match(activity_data_from_sparkl.case_alignments)) {
				resource_copy.standards = activity_data_from_sparkl.case_alignments
				changed = true
			}

			// if we receive a long_description, set resource.long_description to it
			if (activity_data_from_sparkl.long_description) {
				if (resource_copy.long_description != activity_data_from_sparkl.long_description) {
					resource_copy.long_description = activity_data_from_sparkl.long_description
					changed = true
				}

			// else build activity description from the rest of the activity_data_from_sparkl; if changed, set it in resource.long_description
			// (we're not currently using this description, but we might in the future)
			} else if (activity_data_from_sparkl.stars_available) {
				let d = `Student activity with ${activity_data_from_sparkl.stars_available} star${activity_data_from_sparkl.stars_available == 1 ? '' : 's'} available to be earned`
				
				if (resource_copy.long_description != d) {
					resource_copy.long_description = d
					changed = true
				}
			}

			// if open_to_exercise_bank is 'bravo', that means this is sparkl_bank resource; otherwise it's a "normal" sparkl activity
			let resource_type = (activity_data_from_sparkl.open_to_exercise_bank == 'bravo') ? 'sparkl_bank' : 'sparkl'
			if (resource_type != resource_copy.type) {
				console.warn('setting resource type to: ' + resource_type)
				resource_copy.type = resource_type
				changed = true
			}

			// if we changed anything, save the activity resource to the db (if it was a new activity, the sparkl_activity_id/url will have changed from 0 to the newly-created sparkl activity_id, so changed will have been set to true above)
			if (changed) {
				U.loading_start()
				this.$store.dispatch('save_resource', { resource: new Resource(resource_copy) }).then(saved_resource_data => {
					U.loading_stop()

					let r = new Resource(saved_resource_data)
					// emit edit_item_saved to update the activity in whatever collection we're editing
					this.$emit('edit_item_saved', {type:'resource', updated_resource: r})

					// once activity is saved, if sparkl_closed_from_embed is true, finish closing sparkl
					if (this.sparkl_closed_from_embed) this.$nextTick(()=>this.close_sparkl_finish())
				})
				// return here so we don't call close_sparkl_finish too quickly below 
				return
			}

			// if we didn't have to save, and sparkl_closed_from_embed is true, finish closing sparkl here
			if (this.sparkl_closed_from_embed) this.close_sparkl_finish()
		},

		close_sparkl() {
			// // if the user is the owner of the activity, and we're not viewing the original item...
			// Correction: if the user is allowed to edit, and we're not being forced to show the original item...
			if (this.option_availability.edit && !this.force_show_original) {
				U.loading_start()
				// send the host_activity_saved message TO sparkl, so that Sparkl saves anything that might have been edited there
				console.warn('host_activity_saved being called')
				vapp.$refs.sparkl_embed.execute('host_activity_saved', {})

				this.sparkl_closed_from_embed = true
				// ... then once sparkl is done saving, sparkl_activity_saved will be called, and since sparkl_closed_from_embed is true, sparkl_activity_saved will call close_sparkl_finish
			} else {
				this.close_sparkl_finish()
			}
		},

		close_sparkl_finish() {
			U.loading_stop()
			this.sparkl_closed_from_embed = false
			this.force_show_original = false
			vapp.$refs.sparkl_embed.hide_activity()

			// re-show active dialogs/overlays
			$('.v-dialog__content--active, .v-overlay--active').show()
		},

		// this is called from a sparkl_bank resource when the user creates a new activity from the bank
		activity_created_from_bank(data) {
			// create a resource from the data, which should include: sparkl_activity_id, activity_instructions, activity_title, activity_editors, case_alignments
			let r = new Resource({
				resource_id: 'new',
				type: 'sparkl',
				url: data.sparkl_activity_id,
				description: data.activity_title,
				long_description: `Student activity with ${data.stars_available} star${data.stars_available == 1 ? '' : 's'} available to be earned`,
				stars_available: data.stars_available,
				standards: data.case_alignments,
				creator: this.user_info.user_id,
			})

			// save the resource
			this.$store.dispatch('save_resource', {resource: r}).then(saved_resource_data=>{
				// insert the saved resource in the sparkl unit (1) of the user's sandbox
				let saved_resource = new Resource(saved_resource_data)
				this.$store.commit('set', [this.my_default_collection.units[1].resources, 'PUSH', saved_resource])

				// then dispatch save_default_collection_resources; note that it will add the resource to my_resources and take care of my_ca_mappings
				this.$store.dispatch('save_default_collection_resources')
			})
		},

		// this is ONLY called when you're in an existing collection (NOT your default collection) and you say you want to create a copy of an item for your use
		// so we're always adding FROM the "base" collection unit TO your shadow unit here
		async copy_to_my_content(event, flag) {
			// LTI chooser mode: confirm, then choose the activity for use
			if (this.lti_chooser) {
				this.$confirm({
					title: 'Choose Activity for Assignment',
					text: 'Click the button below to choose this activity for your assignment.<div class="mt-2">You can edit the activity from your LMS once it’s linked to your assignment.</div>',
					acceptText: 'Choose Activity',
					acceptIconAfter: 'fas fa-arrow-right',
					acceptColor: 'indigo darken-3',
					cancelColor: '#444',
					dialogMaxWidth: 600,
				}).then(y => {
					this.lti_chooser_choose()
				}).catch(n => {console.error(n)})
				
				return
			}

			// if we're in a my collection or the sandbox, we can't copy to the shadow unit, so just show the share code
			if (!this.option_availability.copy_item_for_my_use || this.viewing_my_default_collection || this.collection_type == 'my') {
				// but, if this is a sparkl and the user is an editor for the collection, don't do this; we'll offer to let them edit below
				if (!(this.is_sparkl && this.lp_context && this.lp_context.user_is_lp_admin())) {
					this.share_item('copy')
					return
				}
			}

			// if user is from an "import_to_district" district, also show the share code
			// CAVEAT: if shift key is held down, do the normal copy_to_my_content 
			if (this.option_availability.import_to_district && !event?.shiftKey) {
				// this.option_availability.import_to_district will hold the title of the app to be shared with
				this.share_item(this.option_availability.import_to_district)
				return
			}

			if (this.is_sparkl) {
				// If we're copying a sparkl, first run synch_user_activities_with_sparkl_server
				// We do this because if a teacher adds an activity to GC or an LMS, a copy of the activity might be made by the teacher or the student clicking the GC/LMS link
				// , and that copy might have been made after the teacher last opened Cureum

				let this_item_id = this.item_id		// see below
				U.loading_start()
				let updated_resources = await this.$store.dispatch('synch_user_activities_with_sparkl_server')
				U.loading_stop()

				// Check to see if if we synched a new Cureum resource copy of the activity.
				// We have to check against this_item_id because if we did sync, `this.item_id` will have reactively switched to the newly-created copy's resource_id
				if (updated_resources && $.isArray(updated_resources) && updated_resources.find(x=>x.resource_template_id == this_item_id)) {
					// if we synched a new copy, just reload sparkl_embed; this will load our copy
					this.show_sparkl('edit', true)
					return
				}

				let title = 'Copy Activity for Your Use'
				// let msg = 'Would you like to make a copy of this activity for your use? This will allow you to edit the activity (if you wish) and use the activity with your students.'
				let msg = 'This is a shared activity created by another user. To use it with your students, you must first copy the activity for your own use.'
				let acceptText = 'Copy Activity'
				let acceptIcon = 'fas fa-copy'
				this.$confirm({
					title: title,
					text: msg,
					acceptText: acceptText,
					acceptIcon: acceptIcon,
					dialogMaxWidth: 600,
				}).then(y => {
					this.copy_to_shadow_unit_finish()
				}).catch(n => {console.error(n)})
			
			// else if lesson
			} else if (this.is_lesson) {
				// open the lesson if it's not already open
				if (!this.lesson_showing) this.card_clicked()
				this.copy_to_shadow_unit_finish()
			}
			// note that we don't do "copy for my use" for non-sparkl resources, because such assets can't be edited by normal users
		},

		copy_to_shadow_unit_finish() {
			// console.warn('copy_to_shadow_unit_finish in ResourceCollectionItem')
			// for lessons and sparkls, call copy_assets_for_adding_to_collection service for the original item; the service (also used in ResourceSearch) will take care of copying underlying sparkl activities
			let payload = {
				user_id: this.user_info.user_id, 
				agency_sanctioned: 'no',	// since the copy is being created for the user, it isn't agency_sanctioned
			}

			if (this.is_lesson) payload.lesson_ids = [this.item.lesson_id]
			else payload.resource_ids = [this.item.resource_id]

			// if we're using a sparkl_origin_override for this collection, send it in
			if (this.lp_context && !empty(this.lp_context.sparkl_origin_override)) {
				payload.sparkl_origin_override = this.lp_context.sparkl_origin_override
			}

			U.loading_start()
			U.ajax('copy_assets_for_adding_to_collection', payload, result => {
				U.loading_stop()
				// if (result.status == 'sparkl_exists') {
				// 	this.$store.commit('set', [this.$store.state.my_resources, 'prop', val])
				// }

				if (result.status != 'ok') { this.$alert(result.status); return; }	// shouldn't happen

				let copied_item
				if (this.is_lesson) copied_item = new Lesson(result.copies[0].lesson_data)
				else copied_item = new Resource(result.copies[0].resource_data)

				// emit copy_to_shadow_unit_finish, which is in CollectionUnit.vue (possibly routing through CollectionResourceFolder) if we're in the context of an LP, or LessonEnhancedMixin if we're in the context of a lesson
				this.$emit('copy_to_shadow_unit_finish', copied_item)

				// and for a sparkl activity, reload the iframe so we see the new activity...
				if (this.is_sparkl) this.show_sparkl('edit', true)
			})
		},

		// immediately make a copy of a sparkl activity for adding to google classroom
		async copy_sparkl_to_shadow_unit_for_gc() {
			let payload = {
				user_id: this.user_info.user_id, 
				agency_sanctioned: 'no',	// since the copy is being created for the user, it isn't agency_sanctioned
				resource_ids: [this.item.resource_id]
			}

			U.loading_start()
			let result = await U.ajax_promise('copy_assets_for_adding_to_collection', payload)
			U.loading_stop()

			if (result.status != 'ok') { this.$alert(result.status); return false; }	// shouldn't happen

			let copied_item = new Resource(result.copies[0].resource_data)

			// emit copy_to_shadow_unit_finish, which should be in CollectionUnit.vue (possibly routing through CollectionResourceFolder)
			this.$emit('copy_to_shadow_unit_finish', copied_item)

			// in this case we *don't* open sparkl; return copied_item for adding to gc
			return copied_item
		},

		rate_resource() {
			U.loading_start()
			setTimeout(x => {
				this.$inform('Thanks for your feedback!')
				U.loading_stop()
			}, 100)
		},

		report_issue() {
			let issue_params = {
				item: this.item,
				learning_progression: this.lp_context,
				lp_unit: this.unit_context,
			}
			vapp.report_issue(issue_params)
		},

		add_to_open_lesson() {
			// this will only be called if we have a lesson open and being edited
			this.lp_context.shifted_for_item.$refs.lesson_editor_component.add_items_from_search([{value:this.item_to_show}])
		},
		
		create_activity_from_resource() {
			// if we're in the context of the enhanced lesson viewer or editor, let it handle this (see LessonEnhancedMixin)
			let lesson_component = U.find_ancestor_vue_component(this, ['LessonView', 'LessonEditor'])
			if (lesson_component) {
				lesson_component.create_activity_from_resource(this.item)
				return
			}

			if (this.$store.getters.simulating_user) { this.$alert('You are not allowed to do this while simulating another user.'); return; }
			this.$prompt({
				title: 'New Student Activity',
				text: 'Enter a student-facing description for the linked resource:',
				initialValue: this.item.description,
				disableForEmptyValue: true,
				acceptText: 'Configure Activity',
				acceptIconAfter: 'fas fa-circle-arrow-right',
			}).then(link_description => {
				link_description = $.trim(link_description)
				if (empty(link_description)) return

				if (this.collection_unit_component) {
					this.collection_unit_component.create_activity_from_resource({type:'sparkl', starting_resource:this.item, link_description: link_description})
				} else this.$alert('Error 38739 in LessonEnhancedMixin')	// shouldn't happen
				// TODO: could allow for adding to sandbox
			}).catch(n => {console.error(n)})

			this.mark_as_last_viewed_resource()
		},

		mark_todo_assignment_complete() {
			if (this.is_required_video) {
				this.$alert('This resource is a video you must watch. Click the link and play the video; when the video completes playing you will be automatically marked as having completed the assignment.')
				return
			}

			if (!this.link_clicked) {
				this.$alert('You must click the link to open and interact with the resource before you can mark that you’ve completed it.')
				return
			}

			this.$confirm({
			    title: 'Confirm Resource Completion',
			    text: sr('<div class="mb-2"><b>$1</b></div>By clicking the button below, you are confirming that you have completed this resource.', this.item.description),
			    acceptText: 'Confirm',
				acceptColor: 'pink accent-4',
				// dialogMaxWidth: 800
			}).then(y => {
				this.todo_complete = true
			}).catch(n => {console.error(n)})
		},

		complete_todo_assignment_clicked() {
			let msg = 'Are you sure you want to mark this resource as not complete?'
			if (this.is_required_video) {
				msg += ' You will have to re-watch the video in order to re-complete the resource assignment.'
			}

			this.$confirm({
			    title: 'Are you sure?',
			    text: msg,
			    acceptText: 'Mark as Not Complete',
			}).then(y => {
				this.todo_complete = false
				this.link_clicked = false
			}).catch(n => {console.error(n)})
		},

		save_video_progress(pct_complete) {
			// save the percent complete as the todo_status
			let payload = {resource_id: this.item.resource_id, todo_status: pct_complete}
			this.$store.dispatch('save_resource_completion', payload)
		},

		video_complete() {
			// this will trigger a save_resource_completion call in the todo_complete computed setter
			this.todo_complete = true
		},

		show_llm_lesson_plan_description() { vapp.show_llm_lesson_plan_description() },

		status_updated(item, type) {
			let args = {type}
			if (type === 'lesson') {
				args.updated_lesson = item
			} else {
				args.updated_resource = item
			}
			this.$emit('edit_item_saved', args)
			const pipeline_statuses = ['submitted', 'under_review', 'ready_for_publication', 'published']
			if (pipeline_statuses.includes(item.publish_status) && this.lesson_being_edited) {
				this.lesson_being_edited.reset_checked_out_for_edit()
				this.lesson_being_edited = null
				this.lesson_editor_showing = false
			}
		},
	}
}
</script>

<style lang="scss">
.k-resource-collection-item {
	margin: 4px;
	background-color: #333;
	border-radius: 10px;
	padding: 1px;
	border: 3px solid transparent;
	position: relative;
	cursor: pointer;
	height: calc(100% - 8px);
	display: flex;

	&:focus,
	&:focus-visible {
		outline: 3px solid #ffffff;
		outline-offset: 3px;
		box-shadow: 0 0 0 2px #0d47a1, 0 0 0 5px #ffffff, 0 0 0 7px #0d47a1;
	}
}

.k-resource-collection-item-type-icon {
	float: right;
	text-align: center;
	height: 18px;
	width: 22px;
	margin: -1px -2px 0 2px;
}

.k-resource-collection-item-menu-btn {
	position: absolute;
	right: 0;
	top: 0;

	.fas { color: #fff !important; }
}

.k-resource-collection-item-menu-btn-preview {
	position: absolute;
	right: 28px;
	top: 0;
}

.k-resource-collection-item-todo-checkbox {
	position: absolute;
	right: 32px;
	top: 2px;
	cursor: pointer;
}

.k-resource-collection-item--title {
	padding: 4px;
	font-size: 14px;
	line-height: 16px;
	text-align: left;
	width: 100%;

	.k-resource-collection-item-title-link {
		// color: #111 !important;
		color: #fff !important;
		font-weight: normal;
		text-decoration: none;
	}
	.fas.fa-circle-user { color:#fff }

	.k-resource-collection-item-title-link:hover {
		text-decoration: underline;
	}
}

.k-portal-collection-minimized .k-resource-collection-item--title {
	margin: 0 12px;
}

.k-resource-collection-item-search-result {
	font-size: 12px !important;
	margin-top: -1px;
	margin-left: 2px;
	color: #fff !important;
}

.k-resource-collection-item-long-title .k-resource-collection-item--title { font-size: 13px; line-height: 15px; }

.k-resource-collection-item-really-long-title .k-resource-collection-item--title { font-size: 12px; line-height: 14px; }

.k-resource-collection-item-standards-wrapper {
	display: flex;
	flex-wrap: wrap;
}

.k-resource-collection-item-show-all-standards {
	.k-resource-collection-item--title {
		flex-wrap: wrap;
	}

	.k-resource-collection-item-standards-wrapper {
		margin-left: auto;
		justify-content: flex-end;

		.k-case-item-btn {
			margin-top: 2px;
			margin-bottom: 2px;
		}
	}
}

.k-resource-collection-item--stats {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin: 4px 2px 2px 2px;
	padding: 2px 0px 2px 8px;
	background-color: rgba(0,0,0,0.05);
	border-radius: 4px;
	font-size: 12px;
}

.k-resource-collection-item--info-header {
	font-size: 14px;
	font-weight: bold;
}

.k-resource-collection-item--actions {
	display: flex;
	justify-content: space-between;
}

.k-resource-collection-item-more-info-card {
	border-width: 5px;
	border-style: solid;
	border-radius: 10px !important;
}

.k-resource-collection-item--unit-planning-guide {
	background-color: $v-green-accent-4;
}

.v-application .k-resource-collection-imported-to-my-collections {
	border-color: rgba(0,0,0,0.6);
}

.v-application .k-resource-collection-item--last-viewed {
	border-color: $v-amber-accent-4;
}

.k-resource-collection-item-quick-look-card-dialog {
	max-height: calc(100vh - 8px) !important;
}

.k-resource-collection-item-lesson-card-dialog, .k-resource-collection-item-resource-view-card-dialog, .k-resource-collection-item-quick-look-card-dialog {
	border-radius: 12px !important;

	.k-resource-link-inline-html {
		max-width: 760px;
		margin-left: auto;
		margin-right: auto;
	}
}

.k-resource-collection-item-quick-look-card-dialog-reslink {
	// this makes it so you can see the banner, and the bottom of the dialog ends 8px above the bottom of the screen
	max-height: calc(100vh - 60px) !important;
	margin-top:68px;
}

.k-resource-collection-lesson-item-card-dialog-shifted {
	position: fixed;
	left: auto;
	right: 0.1vw;
	top: 4px;
	width: 49.7vw;
	margin: 0;
	height: calc(100vh - 8px);
	max-height: calc(100vh - 8px) !important;
	overflow: hidden;

	.k-lpe-alba-menu-stages {
		display: none;
	}

	.k-lpe-lesson-edit-outer,
	.k-lesson-edit-outer {
		flex: 1 1 auto;
		min-height: 0;
		overflow-y: auto;
	}
}

.k-resource-collection-quick-look-item-card-dialog-shifted {
	position: fixed;
	right: auto;
	left: 0.1vw;
	top: 4px;
	width: 49.7vw;
	margin: 0;
	max-height: calc(100vh - 8px) !important;
}

.k-resource-collection-item-lesson-card {
	border-radius: 12px !important;
	padding: 12px;
}

.k-resource-collection-item-long-description {
	background-color: #f0f0f0;
	padding: 8px;
	border-radius: 8px;
	font-size: 16px;
	line-height: 21px;
	width: 800px;
	max-width: 800px;
}

.k-resource-collection-item-extensions {
	border: 1px solid #999;
	padding: 8px;
	margin: 0 12px;
	font-size: 16px;
	border-radius: 8px;
}

.k-resource-link-iframe {
	width: 100%;
	height: 100%;
	border: 0;
	background-color: #fff;
}

.k-resource-link-inline-html {
	background-color: #fff;
	border-radius: 8px;
	padding: 5px 15px 15px 15px;
	font-size: 14px;
}

.k-aspect-ratio-box--10-8 {
	height: 0;
	overflow: hidden;
	padding-top: 10px / 8px * 100%;
	background: white;
	position: relative;
}

.k-aspect-ratio-box-inside {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
}

.k-resource-link-iframe-outer.k-aspect-ratio-box--10-8 {
	// use this so the iframe won't be too tall when it's wide
	padding-top: min(76vh, 125%);
}

.k-resource-link-check-box {
	color: $v-pink-accent-1 !important;
}

.k-resource-link-open-btn-tooltip {
	opacity: 1.0 !important;
}

// rotate the thumbtack icon to make it more "edgy"
.fa-thumbtack {
	transform: rotate(-30deg);
}

// make room for the preview button in the lti chooser
.k-resource-folder-resource-item-full-wrapper .k-resource-collection-item {
	padding-right: 58px !important;
	
	.fa-circle-check {
		font-size: 20px;
		color: $v-indigo-darken-2 !important;
	}	
}

.k-new-indicator {
	height: 20px;
	line-height: 20px;
	width: 57px;
	background-color: #0279ab;
    border-radius: 5px;
    font-weight: bold;
    font-size: 12px;
    padding: 0 8px;
    color: white;

	&.k-tile {
		margin-top: 4px;
	}
}

.k-new-indicator-quick-look {
    height: 24px;
    line-height: 20px;
    width: 62px;
    background-color: #0279ab;
    border-radius: 5px;
    font-weight: bold;
    font-size: 14px;
    padding: 2px 8px;
    color: white;
}
</style>
